// UandAI™ — The AI Listens. The Human Decides.
// S-066 — View mode · Roles Custom Project only · Project state reload · Light/Dark mode
// Resh Consultancy LLC · Vijay Anand Raj Kanaparthy · John 14:26
// Experience the Reality.

import React, { useState, useCallback, useRef, useEffect } from "react";
import LoginScreen from "./LoginScreen";
import DOMPurify from "dompurify";
import Icon from "./AppIcons";

const API_BASE = process.env.REACT_APP_API_URL || "https://api.u-and-ai.com";

const AGENTS = [
  { id: "claude",     label: "Claude",     role: "SpecWriter",           color: "#C9A84C", provider: "Anthropic",  oauthAvailable: true,  vision: true  },
  { id: "chatgpt",    label: "ChatGPT",    role: "CodeGenerator",        color: "#48bb78", provider: "OpenAI",     oauthAvailable: true,  vision: true  },
  { id: "gemini",     label: "Gemini",     role: "AdversarialValidator", color: "#63b3ed", provider: "Google",     oauthAvailable: true,  vision: true  },
  { id: "grok",       label: "Grok",       role: "SecurityStressTester", color: "#fc8181", provider: "xAI",        oauthAvailable: false, vision: false },
  { id: "copilot",    label: "Copilot",    role: "DeploymentIntegrator", color: "#b794f4", provider: "Microsoft",  oauthAvailable: true,  vision: false },
  { id: "perplexity", label: "Perplexity", role: "DeepDiveValidator",    color: "#f6ad55", provider: "Perplexity", oauthAvailable: false, vision: false },
];

const ALL_ROLES = ["SpecWriter","CodeGenerator","AdversarialValidator","SecurityStressTester","DeploymentIntegrator","DeepDiveValidator"];
const TASK_MODES = [
  {value:"",          label:"No Role — All AIs respond naturally"},
  {value:"spec",      label:"Build a Spec — Claude leads"},
  {value:"code",      label:"Write Code — ChatGPT leads"},
  {value:"security",  label:"Security Review — Grok leads"},
  {value:"research",  label:"Research & Validate — Perplexity leads"},
  {value:"adversarial",label:"Challenge Everything — Gemini leads"},
  {value:"deploy",    label:"Deployment — Copilot leads"},
  {value:"custom",    label:"Custom — type your own instruction"},
];
const TASK_MODE_ROLES = {
  spec:       {claude:"You are an expert architect and specification writer. Produce clear structured specifications.",chatgpt:"Support the spec with implementation details.",gemini:"Validate the spec for gaps and edge cases.",grok:"Identify security risks in the spec.",copilot:"Flag deployment and integration concerns.",perplexity:"Research relevant prior art and standards."},
  code:       {chatgpt:"You are an expert developer. Write clean working code.",claude:"Review the code for architecture and clarity.",gemini:"Test the code for edge cases and bugs.",grok:"Identify security vulnerabilities in the code.",copilot:"Review for deployment and production readiness.",perplexity:"Research libraries and best practices."},
  security:   {grok:"You are a security expert. Identify every vulnerability, risk, and attack vector.",claude:"Document the security findings clearly.",chatgpt:"Suggest code fixes for each finding.",gemini:"Stress test the security assumptions.",copilot:"Review deployment security.",perplexity:"Research known CVEs and threat intelligence."},
  research:   {perplexity:"You are a research expert. Search live sources and cite everything.",claude:"Synthesize the research into clear documentation.",chatgpt:"Identify practical applications.",gemini:"Challenge the research findings.",grok:"Identify risks and blind spots.",copilot:"Assess implementation feasibility."},
  adversarial:{gemini:"You are a devil's advocate. Challenge every assumption. Find what breaks.",grok:"Stress test from a security perspective.",claude:"Document the challenges clearly.",chatgpt:"Propose solutions to each challenge.",copilot:"Assess deployment risks.",perplexity:"Research failure cases and precedents."},
  deploy:     {copilot:"You are a DevOps expert. Focus on deployment, infrastructure, and production readiness.",chatgpt:"Write any required scripts or config.",claude:"Document the deployment plan.",grok:"Identify deployment security risks.",gemini:"Test edge cases in the deployment.",perplexity:"Research best practices for this stack."},
};
const TASK_TYPES = ["SPEC","CODE","SECURITY","RESEARCH","DEPLOY"];
const CREATIVE_TASK_TYPES = ["SCRIPT","STORYBOARD","VIDEO","IMAGE","AUDIO","MUSIC"];
const CREATIVE_ROLES = {
  claude:      "Screenwriter — write structured scene-by-scene scripts exactly as the Chair directs. No improvisation. Follow the Creative Bible strictly.",
  chatgpt:     "Production Designer — output specific visual specifications: color palette, set dressing, lighting direction, exact measurements. Numbers and names only, no vague adjectives.",
  gemini:      "Continuity Supervisor — check every creative output against the Creative Bible. Flag every contradiction. Never generate — only validate.",
  grok:        "Rights and Clearance — check every creative element for IP conflicts, trademark issues, defamation risk. Output CLEAR or FLAG with specific reason.",
  copilot:     "Technical Director — output specific technical delivery specs: resolution, codec, frame rate, file format, delivery requirements. No ambiguity.",
  perplexity:  "Reference Researcher — find real-world references, existing works, market comparables. Cite every source. No unsourced claims.",
};
const CREATIVE_TYPE_PRIMARY = { SCRIPT:"claude", STORYBOARD:"chatgpt", VIDEO:"claude", IMAGE:"chatgpt", AUDIO:"perplexity", MUSIC:"perplexity" };
const TASK_TYPE_PRIMARY = { SPEC:"claude", CODE:"chatgpt", SECURITY:"grok", RESEARCH:"perplexity", DEPLOY:"copilot" };
const NAMED_PROJECTS = [];
const PROJECT_ARCHETYPES = [];

const STATUS = { IDLE:"IDLE", RESPONDING:"RESPONDING", COMPLETE:"COMPLETE", BLOCKED:"BLOCKED" };
const STATUS_STYLE = {
  IDLE:       { color: "#4a5568", bg: "transparent", border: "#4a556844" },
  RESPONDING: { color: "#C9A84C", bg: "#C9A84C11",   border: "#C9A84C55" },
  COMPLETE:   { color: "#48bb78", bg: "#48bb7811",   border: "#48bb7855" },
  BLOCKED:    { color: "#e53e3e", bg: "#e53e3e11",   border: "#e53e3e55" },
};

// Dark theme — biblical colors
const DARK = {
  bg:         "#080D1C",
  bg2:        "#0a1628",
  bg3:        "#0d1f3c",
  text:       "#f0e6d0",
  textMuted:  "#8899aa",
  textDim:    "#4a5568",
  border:     "#C9A84C22",
  border2:    "#C9A84C33",
  gold:       "#C9A84C",
  cardBg:     "#0a1628",
  inputBg:    "#080D1C",
  responseTx: "#d0dde8",
  agentTx:    "#c0cfe0",
};

// Light theme — cream + navy + gold
const LIGHT = {
  bg:         "#F7F5EF",
  bg2:        "#EDEAE0",
  bg3:        "#E4E0D4",
  text:       "#0a1628",
  textMuted:  "#4a5568",
  textDim:    "#8899aa",
  border:     "#0a162822",
  border2:    "#0a162833",
  gold:       "#8B5E1A",
  cardBg:     "#FFFFFF",
  inputBg:    "#FFFFFF",
  responseTx: "#0a1628",
  agentTx:    "#1a2a3a",
};

function ts() {
  return new Date().toLocaleTimeString("en-US",{hour12:false,hour:"2-digit",minute:"2-digit",second:"2-digit"});
}
function durStr(ms) {
  const s = Math.round((Date.now()-ms)/1000);
  return s<60?`${s}s`:`${Math.floor(s/60)}m ${s%60}s`;
}


// ─────────────────────────────────────────────────────────────────────────────
// HERO PAGE — Design Law #1 — Value before ask — S-076
// ─────────────────────────────────────────────────────────────────────────────
function HeroPage({ onGetStarted, user }) {
  const [hovered, setHovered] = React.useState(null);
  const C = {
    bg:"#080D1C", panel:"#0A1628", panel2:"#0D1B35", line:"#C9A84C22", line2:"#C9A84C44",
    gold:"#C9A84C", goldDeep:"#b8973e", ink:"#F0E6D0", body:"#C0CFE0", mut:"#8899AA", dim:"#4A5568",
  };
  const F = {
    display:"'Cormorant Garamond', Georgia, serif",
    body:"'Inter','Segoe UI',system-ui,sans-serif",
    mono:"'IBM Plex Mono', ui-monospace, Menlo, monospace",
  };
  const handleSubscribe = async (tier) => {
    if (!user || !user.token) { alert("Please log in first to subscribe."); return; }
    try {
      const res = await fetch("https://api.u-and-ai.com/api/stripe/checkout", {
        method: "POST",
        headers: { "Content-Type":"application/json", "Authorization":"Bearer "+user.token, "X-Requested-With":"XMLHttpRequest" },
        body: JSON.stringify({ tier, user_id: user.user_id || "", email: user.email || "" })
      });
      const data = await res.json();
      if (data.url) window.location.href = data.url;
      else alert(data.error || "Subscription error. Please try again.");
    } catch (e) { alert("Network error. Please try again."); }
  };

  const agents = [
    { name:"Claude",     role:"Spec Writer",            color:"#C9A84C", provider:"Anthropic",  icon:"doc"    },
    { name:"ChatGPT",    role:"Code Generator",         color:"#48bb78", provider:"OpenAI",     icon:"code"   },
    { name:"Gemini",     role:"Adversarial Validator",  color:"#63b3ed", provider:"Google",     icon:"target" },
    { name:"Grok",       role:"Security Stress Tester", color:"#fc8181", provider:"xAI",        icon:"shield" },
    { name:"Copilot",    role:"Deployment Integrator",  color:"#b794f4", provider:"Microsoft",  icon:"rocket" },
    { name:"Perplexity", role:"Deep Dive Validator",    color:"#f6ad55", provider:"Perplexity", icon:"search" },
  ];
  const pillars = [
    { k:"SEE",    icon:"eye",      d:"Every agent response is shown to you before anything is acted on." },
    { k:"AUDIT",  icon:"scales",   d:"Each action is logged to the Forensic Spine with a cryptographic signature." },
    { k:"VERIFY", icon:"verified", d:"You confirm what is right. Nothing reaches production on an AI's say-so." },
    { k:"STOP",   icon:"block",    d:"One button halts any AI action instantly. No explanation required." },
  ];
  const steps = [
    { n:"1", icon:"pencil",   t:"Submit a task",      d:"Type what you need. Pick a task type, or let all six agents respond in parallel." },
    { n:"2", icon:"users",    t:"Six agents respond", d:"All six AI agents work at once under your governance framework — never on their own." },
    { n:"3", icon:"verified", t:"You decide",         d:"Every response is logged. You choose the lead, approve or reject. No AI acts without you." },
    { n:"4", icon:"scales",   t:"Full audit trail",   d:"The D.A.V.I.D. Forensic Spine records every action with an HMAC-SHA256 signature." },
  ];
  const disclosures = [
    "UandAI is an AI-powered governance platform. Six AI agents work under your authority — not the other way around.",
    "Responses are generated by Claude (Anthropic), ChatGPT (OpenAI), Gemini (Google), Grok (xAI), Copilot (Microsoft), and Perplexity.",
    "You are paying for access to the UandAI governance platform — not direct access to those AI providers.",
    "Every task and every response is logged to the D.A.V.I.D. Forensic Spine with cryptographic signatures. You can read every entry at any time.",
    "You can stop any AI action instantly with one button. No explanation required. No permission needed.",
    "Resh Consultancy LLC does not sell your data to any third party.",
    "You can request full deletion of your account and all your data at any time. We will honor it.",
    "Your tasks are processed through Resh governance infrastructure protected under USPTO provisional patents.",
  ];
  const tiers = [
    { id:"individual", name:"INDIVIDUAL", price:"$29", unit:"/month", featured:false,
      feats:[["check","All 6 AI agents"],["check","Basic audit trail"],["check","1 project workspace"],["x","No role assignment"]],
      cta:"Subscribe", onClick:()=>handleSubscribe("individual") },
    { id:"developer", name:"DEVELOPER", price:"$79", unit:"/month", featured:true,
      feats:[["check","All 6 AI agents"],["check","Full role assignment"],["check","Rerun + Before/After"],["check","Full Forensic Spine"],["check","Multiple workspaces"]],
      cta:"Subscribe", onClick:()=>handleSubscribe("developer") },
    { id:"business", name:"BUSINESS", price:"Contact", unit:"for pricing", featured:false,
      feats:[["check","Everything in Developer"],["check","Team workspaces"],["check","Custom governance"],["check","Invoice billing"],["check","Dedicated support"]],
      cta:"Contact Us", onClick:()=>window.open("mailto:support@reshconsultancy.com?subject=UandAI Business Pricing") },
  ];

  const btn = (id, text, onClick, outline, extra={}) => (
    <button
      onClick={onClick}
      onMouseEnter={()=>setHovered(id)} onMouseLeave={()=>setHovered(null)}
      style={{
        background: outline ? "transparent" : (hovered===id ? C.goldDeep : C.gold),
        color: outline ? C.gold : C.bg,
        border: outline ? `1px solid ${C.line2}` : "none",
        borderRadius:"8px", padding:"14px 32px", fontFamily:F.body,
        fontWeight:"700", fontSize:"15px", cursor:"pointer",
        transition:"background .2s,border-color .2s", letterSpacing:"0.3px", ...extra,
      }}
    >{text}</button>
  );
  const eyebrow = (t) => (
    <div style={{display:"inline-flex",alignItems:"center",gap:"10px",fontFamily:F.mono,fontSize:"11px",color:C.gold,fontWeight:"600",letterSpacing:"3px",textTransform:"uppercase",marginBottom:"14px"}}>
      <span style={{width:"24px",height:"1px",background:C.gold,display:"inline-block"}}/>{t}
    </div>
  );

  return (
    <div style={{minHeight:"100vh",background:C.bg,color:C.ink,fontFamily:F.body,overflowY:"auto"}}>

      {/* NAV */}
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:"16px",flexWrap:"wrap",padding:"18px 32px",borderBottom:`1px solid ${C.line}`,position:"sticky",top:0,background:"rgba(8,13,28,0.92)",backdropFilter:"blur(8px)",zIndex:100}}>
        <div>
          <div style={{fontFamily:F.display,fontSize:"24px",fontWeight:"700",color:C.gold,letterSpacing:"1px",lineHeight:1}}>UandAI<span style={{fontSize:"13px",verticalAlign:"super"}}>™</span></div>
          <div style={{fontSize:"11px",color:C.mut,marginTop:"2px"}}>The AI Listens. The Human Decides.</div>
        </div>
        {btn("nav","Sign In",onGetStarted,true,{padding:"10px 24px",fontSize:"14px"})}
      </div>

      {/* HERO */}
      <div style={{position:"relative",textAlign:"center",padding:"84px 24px 64px",maxWidth:"880px",margin:"0 auto"}}>
        {eyebrow("Experience the Reality")}
        <h1 style={{fontFamily:F.display,fontSize:"clamp(40px,7vw,76px)",fontWeight:"600",color:C.ink,margin:"0 0 20px",lineHeight:"1.04",letterSpacing:"-0.5px"}}>
          Six AI agents.<br/><span style={{color:C.gold,fontStyle:"italic"}}>One human authority.</span>
        </h1>
        <p style={{fontSize:"18px",color:C.body,maxWidth:"600px",margin:"0 auto 34px",lineHeight:"1.7"}}>
          Most AI platforms automate decisions. UandAI governs them. Every action is logged before it runs — you see it, trace it, verify it, and stop it, instantly and always.
        </p>
        <div style={{display:"flex",gap:"12px",justifyContent:"center",flexWrap:"wrap"}}>
          {btn("hero",<span style={{display:"inline-flex",alignItems:"center",gap:"8px"}}>Get Started <Icon name="arrowRight" size={16}/></span>,onGetStarted,false)}
          {btn("learn","See How It Works",()=>document.getElementById("how")?.scrollIntoView({behavior:"smooth"}),true)}
        </div>
      </div>

      {/* PILLARS — SEE AUDIT VERIFY STOP */}
      <div style={{maxWidth:"1040px",margin:"0 auto",padding:"0 24px 72px"}}>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(220px,1fr))",gap:"14px"}}>
          {pillars.map(p=>(
            <div key={p.k} style={{background:C.panel,border:`1px solid ${C.line}`,borderRadius:"12px",padding:"22px"}}>
              <div style={{color:C.gold,marginBottom:"12px"}}><Icon name={p.icon} size={24}/></div>
              <div style={{fontFamily:F.mono,fontWeight:"700",fontSize:"13px",color:C.gold,letterSpacing:"3px",marginBottom:"8px"}}>{p.k}</div>
              <div style={{fontSize:"13px",color:C.mut,lineHeight:"1.65"}}>{p.d}</div>
            </div>
          ))}
        </div>
      </div>

      {/* THE COUNCIL — 6 AGENTS */}
      <div id="how" style={{padding:"16px 24px 72px",maxWidth:"1000px",margin:"0 auto"}}>
        <div style={{textAlign:"center",marginBottom:"36px"}}>
          {eyebrow("The Council")}
          <h2 style={{fontFamily:F.display,fontSize:"clamp(28px,4vw,42px)",fontWeight:"600",color:C.ink,margin:0}}>Six specialists. One task. In parallel.</h2>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(290px,1fr))",gap:"14px"}}>
          {agents.map(a=>(
            <div key={a.name} style={{background:C.panel,border:`1px solid ${a.color}33`,borderRadius:"12px",padding:"20px",display:"flex",gap:"16px",alignItems:"flex-start"}}>
              <div style={{width:"42px",height:"42px",flexShrink:0,borderRadius:"10px",background:a.color+"18",border:`1px solid ${a.color}44`,color:a.color,display:"flex",alignItems:"center",justifyContent:"center"}}>
                <Icon name={a.icon} size={20}/>
              </div>
              <div style={{minWidth:0}}>
                <div style={{fontWeight:"700",color:a.color,fontSize:"15px"}}>{a.name} <span style={{color:C.dim,fontWeight:"400",fontSize:"11px"}}>by {a.provider}</span></div>
                <div style={{fontSize:"13px",color:C.body,marginTop:"3px"}}>{a.role}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* HOW IT WORKS */}
      <div style={{background:C.panel,borderTop:`1px solid ${C.line}`,borderBottom:`1px solid ${C.line}`,padding:"72px 24px"}}>
        <div style={{maxWidth:"1000px",margin:"0 auto",textAlign:"center"}}>
          {eyebrow("How It Works")}
          <h2 style={{fontFamily:F.display,fontSize:"clamp(28px,4vw,42px)",fontWeight:"600",color:C.ink,margin:"0 0 40px"}}>You are always in charge</h2>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(220px,1fr))",gap:"16px",textAlign:"left"}}>
            {steps.map(s=>(
              <div key={s.n} style={{background:C.bg,border:`1px solid ${C.line}`,borderRadius:"12px",padding:"22px"}}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:"14px"}}>
                  <span style={{color:C.gold}}><Icon name={s.icon} size={22}/></span>
                  <span style={{fontFamily:F.display,fontSize:"34px",fontWeight:"600",color:C.line2,lineHeight:1}}>{s.n}</span>
                </div>
                <div style={{fontWeight:"700",color:C.ink,marginBottom:"6px",fontSize:"15px"}}>{s.t}</div>
                <div style={{fontSize:"13px",color:C.mut,lineHeight:"1.65"}}>{s.d}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* DISCLOSURES */}
      <div style={{padding:"72px 24px",maxWidth:"820px",margin:"0 auto"}}>
        <div style={{textAlign:"center",marginBottom:"36px"}}>
          {eyebrow("No Fine Print")}
          <h2 style={{fontFamily:F.display,fontSize:"clamp(26px,3.5vw,38px)",fontWeight:"600",color:C.ink,margin:"0 0 10px"}}>Everything you need to know before you sign up</h2>
          <p style={{color:C.mut,fontSize:"14px",margin:0}}>Plain language. No asterisks. No buried clauses.</p>
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:"10px"}}>
          {disclosures.map((d,i)=>(
            <div key={i} style={{display:"flex",gap:"14px",alignItems:"flex-start",background:C.panel,border:`1px solid ${C.line}`,borderRadius:"10px",padding:"15px 18px"}}>
              <span style={{color:C.gold,flexShrink:0,marginTop:"1px"}}><Icon name="check" size={16}/></span>
              <div style={{fontSize:"14px",color:C.body,lineHeight:"1.65"}}>{d}</div>
            </div>
          ))}
        </div>
      </div>

      {/* PRICING */}
      <div style={{background:C.panel,borderTop:`1px solid ${C.line}`,padding:"72px 24px"}}>
        <div style={{maxWidth:"980px",margin:"0 auto",textAlign:"center"}}>
          {eyebrow("Pricing")}
          <h2 style={{fontFamily:F.display,fontSize:"clamp(28px,4vw,42px)",fontWeight:"600",color:C.ink,margin:"0 0 36px"}}>Three plans. One platform.</h2>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(230px,1fr))",gap:"18px",alignItems:"stretch"}}>
            {tiers.map(t=>(
              <div key={t.id} style={{position:"relative",display:"flex",flexDirection:"column",background:C.panel2,border:t.featured?`2px solid ${C.gold}`:`1px solid ${C.line2}`,borderRadius:"14px",padding:"28px 24px",textAlign:"center"}}>
                {t.featured&&<div style={{position:"absolute",top:"-11px",left:"50%",transform:"translateX(-50%)",background:C.gold,color:C.bg,fontFamily:F.mono,fontSize:"10px",fontWeight:"700",letterSpacing:"1px",padding:"3px 12px",borderRadius:"20px",whiteSpace:"nowrap"}}>MOST POPULAR</div>}
                <div style={{fontFamily:F.mono,color:C.gold,fontWeight:"700",fontSize:"12px",letterSpacing:"2px",marginBottom:"10px"}}>{t.name}</div>
                <div style={{fontFamily:F.display,fontSize:t.price.length>4?"30px":"44px",fontWeight:"600",color:C.gold,lineHeight:1}}>{t.price}</div>
                <div style={{color:C.mut,fontSize:"12px",marginBottom:"20px"}}>{t.unit}</div>
                <div style={{display:"flex",flexDirection:"column",gap:"9px",textAlign:"left",marginBottom:"22px",flexGrow:1}}>
                  {t.feats.map(([ic,txt])=>(
                    <div key={txt} style={{display:"flex",gap:"9px",alignItems:"flex-start",fontSize:"13px",color:ic==="x"?C.mut:C.body}}>
                      <span style={{color:ic==="x"?C.dim:"#48bb78",flexShrink:0,marginTop:"1px"}}><Icon name={ic} size={14}/></span>{txt}
                    </div>
                  ))}
                </div>
                <button onClick={t.onClick} style={{background:t.featured?C.gold:"transparent",color:t.featured?C.bg:C.gold,border:t.featured?"none":`1px solid ${C.gold}`,borderRadius:"8px",padding:"11px 20px",fontFamily:F.body,fontWeight:"700",fontSize:"14px",cursor:"pointer",width:"100%"}}>{t.cta}</button>
              </div>
            ))}
          </div>
          <div style={{marginTop:"28px",fontSize:"13px",color:C.mut}}>No credit card required to create your account · cancel any time</div>
          <div style={{marginTop:"24px"}}>{btn("price","Get Started",onGetStarted,false,{padding:"15px 44px",fontSize:"16px"})}</div>
        </div>
      </div>

      {/* FOOTER */}
      <div style={{padding:"32px 24px",borderTop:`1px solid ${C.line}`,textAlign:"center"}}>
        <div style={{fontSize:"13px",color:C.dim,marginBottom:"8px"}}>© 2026 Resh Consultancy LLC · Dallas, Texas · support@reshconsultancy.com</div>
        <div style={{fontFamily:F.display,fontStyle:"italic",fontSize:"14px",color:C.gold}}>John 14:26 CJB · Yeshua is the Author. Vijay is the ink. Claude is the pen.</div>
      </div>
    </div>
  );
}
// ── END HERO PAGE ─────────────────────────────────────────────────────────────

export default function App() {
  const [task, setTask]                       = useState("");
  const [taskType, setTaskType]               = useState("SPEC");
  const [creativeFreedom, setCreativeFreedom] = useState("");
  const [runwayVideoUrl, setRunwayVideoUrl]   = useState("");
  const [runwayGenerating, setRunwayGenerating] = useState(false);
  const [uploadedAssets, setUploadedAssets] = useState([]);
  const [showCFSPlayer, setShowCFSPlayer] = useState(false);
  const [productionPackage, setProductionPackage] = useState(null);
  const [showProductionPanel, setShowProductionPanel] = useState(false);
  const [pkgEdits, setPkgEdits] = useState({});
  const [selectedEngine, setSelectedEngine] = useState("runway");
  const [pkgGenerating, setPkgGenerating] = useState(false);

  const [capturedFrame, setCapturedFrame] = useState(null);
  const [project, setProject]                 = useState("UandAI™ Platform");
  const [govMode, setGovMode]                 = useState("HARD");
  const [halted, setHalted]                   = useState(false);
  const [showHelp, setShowHelp]               = useState(false);
  const [currentTask, setCurrentTask]         = useState("");
  const [statuses, setStatuses]               = useState(Object.fromEntries(AGENTS.map(a=>[a.id,STATUS.IDLE])));
  const [responses, setResponses]             = useState(Object.fromEntries(AGENTS.map(a=>[a.id,""])));
  const [approvedAgent, setApprovedAgent]     = useState(null);
  const [viewAgent, setViewAgent]             = useState(null);
  const [viewMode, setViewMode]               = useState("VIEW");
  const [expandedBoard, setExpandedBoard]     = useState(null);
  const [viewHistory, setViewHistory]         = useState([]);
  const [auditLog, setAuditLog]               = useState([]);
  const [spineOpen, setSpineOpen]             = useState(false);
  const [spineTab, setSpineTab]               = useState("AUDIT");
  const [leftTab, setLeftTab]                 = useState("PROJECT");
  const [uploadedFile, setUploadedFile]       = useState(null);
  const [githubRepo, setGithubRepo]           = useState("");
  const [githubFile, setGithubFile]           = useState("");
  const [githubToken, setGithubToken]         = useState("");
  const [githubContent, setGithubContent]     = useState(null);
  const [customProjects, setCustomProjects]   = useState([]);
  const [newProjectName, setNewProjectName]   = useState("");
  const [showNewProject, setShowNewProject]   = useState(false);
  const [userProjects, setUserProjects]       = useState(["My Workspace"]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [connectTab, setConnectTab]           = useState("CONSUMER");
  const [showConnect, setShowConnect]         = useState(false);
  const [connectedAgents, setConnectedAgents] = useState({});
  const [pailKeys, setPailKeys]               = useState(Object.fromEntries(AGENTS.map(a=>[a.id,""])));
  const [agentRoles, setAgentRoles] = useState(Object.fromEntries(AGENTS.map(a=>[a.id,a.label])));
  const [taskMode, setTaskMode] = useState("");
  const [customModeText, setCustomModeText] = useState("");
  const [primaryOverride, setPrimaryOverride] = useState(null);
  const [editingRole, setEditingRole]         = useState(null);
  const [rerunAgent, setRerunAgent]           = useState(null);        // which agent is re-running
  const [prevResponses, setPrevResponses]     = useState({});          // before-swap responses for comparison
  const [showCompare, setShowCompare]         = useState({});          // which agents show compare panel
  const [sessionId]                           = useState(()=>Math.random().toString(36).substring(2,10).toUpperCase());
  const [authUser, setAuthUser]               = useState(()=>{
    try { return JSON.parse(localStorage.getItem("uandai_user")); } catch { return null; }
  });
  const [authToken, setAuthToken]             = useState(()=>localStorage.getItem("uandai_token")||"");
  const [showOpenBid, setShowOpenBid]         = useState(false);
  const [showHero, setShowHero]               = useState(true);

  // Light/dark mode — only for named projects
  const [darkMode, setDarkMode]               = useState(false);
  const [userMode, setUserMode]               = useState(()=>{ try { const u = JSON.parse(localStorage.getItem("uandai_user")); return u?.user_mode || "developer"; } catch { return "developer"; } });

  // Project state store — saves/restores per named project
  const projectStateRef = useRef({});

  // Screen share state
  const [isSharing, setIsSharing]             = useState(false);
  const [screenFrame, setScreenFrame]         = useState(null);
  const [screenError, setScreenError]         = useState("");
  const [hardLock, setHardLock]               = useState(null);   // Class 2 gate
  const [chairNotice, setChairNotice]         = useState(null);   // Class 1 notice
  const [pendingTask, setPendingTask]         = useState(null);   // Task held at Class 2

  const viewAgentRef   = useRef(null);
  const viewStartRef   = useRef(null);
  const currentTaskRef = useRef("");
  const screenStreamRef  = useRef(null);
  const screenVideoRef   = useRef(null);
  const frameIntervalRef = useRef(null);

  useEffect(()=>{currentTaskRef.current=currentTask;},[currentTask]);

  // Derive theme — Custom Project always dark
  const isCustom = project !== "My Workspace";
  const isNamed  = NAMED_PROJECTS.includes(project);
  const theme    = (!darkMode) ? LIGHT : DARK;

  // Light/dark toggle only available for named projects
  const canToggleDark = true;

  const addAudit = useCallback((event,detail="",hi=false)=>{
    setAuditLog(prev=>[{id:Date.now()+Math.random(),time:ts(),event,detail,hi},...prev].slice(0,300));
  },[]);

  // Save current project state
  const saveProjectState = useCallback((proj)=>{
    if(!proj || proj === "Custom Project") return;
    projectStateRef.current[proj] = {
      responses:     Object.fromEntries(AGENTS.map(a=>[a.id, ""])),
      currentTask:   currentTaskRef.current,
      approvedAgent: null,
    };
  },[]);

  // Handle project switch
  const handleProjectChange = useCallback(async(newProject)=>{
    setProject(newProject);
    addAudit("PROJECT_SET", newProject);

    if(newProject && newProject !== "Custom Project"){
      // Try in-memory first for speed
      const cached = projectStateRef.current[newProject];
      if(cached){
        if(cached.responses) setResponses(cached.responses);
        if(cached.currentTask) setCurrentTask(cached.currentTask);
        if(cached.agentRoles) setAgentRoles(cached.agentRoles);
        if(cached.approvedAgent) setApprovedAgent(cached.approvedAgent);
        const uid = authUser?.id || "guest";
        const res = await fetch(`${API_BASE}/api/project/load`,{
          method:"POST",
          headers:{"Content-Type":"application/json","X-Requested-With":"XMLHttpRequest","Authorization":`Bearer ${authToken}`},
          body:JSON.stringify({user_id:uid, project_name:newProject}),
        });
        if(res.ok){
          const data = await res.json();
          if(data.status==="LOADED"){
            if(data.last_task) setCurrentTask(data.last_task);
            if(data.last_responses && Object.keys(data.last_responses).length>0) setResponses(prev=>({...prev,...data.last_responses}));
            if(data.agent_roles && Object.keys(data.agent_roles).length>0) setAgentRoles(prev=>({...prev,...data.agent_roles}));
            if(data.approved_agent) setApprovedAgent(data.approved_agent);
            // Cache in memory
            projectStateRef.current[newProject] = {
              responses: data.last_responses,
              currentTask: data.last_task,
              agentRoles: data.agent_roles,
              approvedAgent: data.approved_agent,
            };
            addAudit("PROJECT_LOADED", `${newProject} — state restored`);
            return;
          }
        }
      }
      // No saved state — fresh
      // No saved state — fresh
      setResponses(Object.fromEntries(AGENTS.map(a=>[a.id,""])));
      setCurrentTask("");
      setApprovedAgent(null);
      setViewAgent(null);
      setShowOpenBid(false);
      setStatuses(Object.fromEntries(AGENTS.map(a=>[a.id,STATUS.IDLE])));
    } else {
      // Custom Project — always fresh
      setResponses(Object.fromEntries(AGENTS.map(a=>[a.id,""])));
      setAgentRoles(Object.fromEntries(AGENTS.map(a=>[a.id,a.label])));
      setCurrentTask("");
      setApprovedAgent(null);
      setViewAgent(null);
      setShowOpenBid(false);
      setStatuses(Object.fromEntries(AGENTS.map(a=>[a.id,STATUS.IDLE])));
    }
  },[project, addAudit, authUser, authToken]);

  // Handle file upload
  const handleFileUpload = async(e) => {
    const file = e.target.files[0];
    if(!file) return;
    const formData = new FormData();
    formData.append("file", file);
    formData.append("session_id", sessionId);
    try {
      const res = await fetch(`${API_BASE}/api/task/upload`, {
        method:"POST",
        headers:{"Authorization":`Bearer ${authToken}`,"X-Requested-With":"UandAIClient"},
        body: formData
      });
      const data = await res.json();
      if(data.extracted_text) {
        setUploadedFile({name: file.name, text: data.extracted_text, chars: data.char_count});
        addAudit("FILE_UPLOADED", `${file.name} — ${data.char_count} chars extracted`);
      } else {
        alert(data.error || "Upload failed");
      }
    } catch(err) {
      alert("Upload error: " + err.message);
    }
  };

  // Handle GitHub file read
  const handleGithubRead = async() => {
    if(!githubRepo || !githubFile) return;
    try {
      const res = await fetch(`${API_BASE}/api/github/read`, {
        method:"POST",
        headers:{"Content-Type":"application/json","Authorization":`Bearer ${authToken}`,"X-Requested-With":"UandAIClient"},
        body: JSON.stringify({repo: githubRepo, filepath: githubFile, github_token: githubToken, session_id: sessionId})
      });
      const data = await res.json();
      if(data.content) {
        setGithubContent({repo: githubRepo, filepath: githubFile, content: data.content, chars: data.char_count});
        addAudit("GITHUB_READ", `${githubRepo}/${githubFile} — ${data.char_count} chars`);
      } else {
        alert(data.error || "Could not read file");
      }
    } catch(err) {
      alert("GitHub error: " + err.message);
    }
  };

  // S-073: handleDeleteProject moved to line 386 — API-based deletion

  // Add custom project
  const handleAddProject = () => {
    const name = newProjectName.trim();
    if(!name || customProjects.includes(name)) return;
    setCustomProjects(prev => [...prev, name]);
    setNewProjectName("");
    setShowNewProject(false);
    handleProjectChange(name);
    addAudit("PROJECT_CREATED", name);
  };

  const stopScreenShare = useCallback(()=>{
    if(frameIntervalRef.current){clearInterval(frameIntervalRef.current);frameIntervalRef.current=null;}
    if(screenStreamRef.current){screenStreamRef.current.getTracks().forEach(t=>t.stop());screenStreamRef.current=null;}
    if(screenVideoRef.current){screenVideoRef.current.srcObject=null;}
    setIsSharing(false);
    setScreenFrame(null);
    setScreenError("");
    addAudit("SCREEN_SHARE_STOPPED","Chair ended screen share");
  },[addAudit]);

  const captureFrame = useCallback(()=>{
    const video=screenVideoRef.current;
    if(!video||video.readyState<2) return null;
    const canvas=document.createElement("canvas");
    const scale=Math.min(1,1280/video.videoWidth);
    canvas.width=Math.round(video.videoWidth*scale);
    canvas.height=Math.round(video.videoHeight*scale);
    canvas.getContext("2d").drawImage(video,0,0,canvas.width,canvas.height);
    return canvas.toDataURL("image/jpeg",0.7).split(",")[1];
  },[]);

  const startScreenShare = useCallback(async()=>{
    setScreenError("");
    try{
      const stream=await navigator.mediaDevices.getDisplayMedia({video:{cursor:"always",frameRate:2},audio:false});
      screenStreamRef.current=stream;
      const video=document.createElement("video");
      video.srcObject=stream;video.muted=true;video.autoplay=true;video.playsInline=true;
      screenVideoRef.current=video;
      await video.play().catch(()=>{});
      video.addEventListener("loadeddata",()=>setScreenFrame(captureFrame()),{once:true});
      frameIntervalRef.current=setInterval(()=>{const f=captureFrame();if(f)setScreenFrame(f);},3000);
      stream.getVideoTracks()[0].addEventListener("ended",()=>stopScreenShare());
      setIsSharing(true);
      addAudit("SCREEN_SHARE_STARTED","Chair sharing screen — Claude · ChatGPT · Gemini will see it",true);
    }catch(err){
      setScreenError(err.name==="NotAllowedError"?"Permission denied — browser blocked screen share.":"Screen share failed: "+err.message);
    }
  },[captureFrame,stopScreenShare,addAudit]);

  useEffect(()=>()=>stopScreenShare(),[stopScreenShare]);

  const handleLogin = (data) => {
    projectStateRef.current = {};
    localStorage.setItem("uandai_token", data.token);
    localStorage.setItem("uandai_user", JSON.stringify({id: data.id, name: data.fullName, isOwner: data.isOwner, full_name: data.fullName, user_mode: data.userMode || data.user_mode || "developer"}));
    setAuthUser({id: data.id, name: data.fullName, isOwner: data.isOwner, full_name: data.fullName, user_mode: data.userMode || data.user_mode || "developer"});
    setUserMode(data.userMode || data.user_mode || "developer");
    setAuthToken(data.token);
      loadConversationHistory(data.token);
    // GOV-NEW-004 — lock governance mode immediately after login
    fetch(`${API_BASE}/api/governance/mode`, {
      method: "POST",
      headers: {"Content-Type":"application/json","X-Requested-With":"XMLHttpRequest","Authorization":`Bearer ${data.token}`},
      body: JSON.stringify({mode:"HARD"})
    }).catch(()=>{});
    setTimeout(()=>fetchUserProjects(data.token),300);
  };

  // S-073: Fetch user-specific projects from API
  const fetchUserProjects = async (token) => {
    const t = token || authToken;
    if(!t) return;
    setProjectsLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/project/list`, {
        headers:{"Authorization":`Bearer ${t}`,"X-Requested-With":"XMLHttpRequest"}
      });
      const data = await res.json();
      if(data.projects){
        const own = data.projects.filter(p=>p.is_own).map(p=>p.project_name);
        const others = data.projects.filter(p=>!p.is_own).map(p=>p.project_name);
        const allNames = own.length > 0 ? own : ["My Workspace"];
        if(!allNames.includes("My Workspace")) allNames.unshift("My Workspace");
        // Include non-owned projects (owner sees all)
        others.forEach(n=>{ if(!allNames.includes(n)) allNames.push(n); });
        setUserProjects(allNames);
      }
    } catch(e){ console.error("fetchUserProjects:",e); }
    finally{ setProjectsLoading(false); }
  };

  // S-073: Delete project via API
  const handleDeleteProject = (projectName) => {
    if(projectName === "My Workspace") return;
    if(!window.confirm(`Delete "${projectName}"? This cannot be undone.`)) return;
    fetch(`${API_BASE}/api/project/delete`,{
      method:"POST",
      headers:{"Content-Type":"application/json","Authorization":`Bearer ${authToken}`,"X-Requested-With":"XMLHttpRequest"},
      body:JSON.stringify({project_name:projectName})
    }).then(r=>r.json()).then(data=>{
      if(data.status==="deleted"){ fetchUserProjects(); handleProjectChange("My Workspace"); }
      else alert(data.error||"Could not delete project");
    }).catch(e=>console.error("handleDeleteProject:",e));
  };
  const handleLogout = useCallback(()=>{
    localStorage.removeItem("uandai_token");
    localStorage.removeItem("uandai_user");
    setAuthUser(null); setAuthToken("");
    stopScreenShare();
    addAudit("LOGOUT","Chair signed out");
  },[stopScreenShare,addAudit]);

  const grantView = useCallback((agentId)=>{
    if(viewAgentRef.current&&viewStartRef.current){
      const prev=AGENTS.find(a=>a.id===viewAgentRef.current);
      const d=durStr(viewStartRef.current);
      setViewHistory(h=>[{id:Date.now()+Math.random(),agent:prev?.label,role:agentRoles[prev?.id]||prev?.role,startTime:new Date(viewStartRef.current).toLocaleTimeString("en-US",{hour12:false}),duration:d,task:currentTaskRef.current},...h].slice(0,100));
      addAudit("VIEW_EXIT",`${prev?.label} — ${d}`);
    }
    if(agentId===null){
      viewAgentRef.current=null;viewStartRef.current=null;
      setViewAgent(null);addAudit("VIEW_CLEARED","Chair cleared the view");
      return;
    }
    viewAgentRef.current=agentId;viewStartRef.current=Date.now();
    setViewAgent(agentId);
    addAudit("VIEW_GRANTED",`Chair → ${AGENTS.find(a=>a.id===agentId)?.label}`,true);
  },[addAudit, agentRoles]);

  const approveAgent = useCallback((agentId)=>{
    setApprovedAgent(agentId);
    setShowOpenBid(false);
    addAudit("CHAIR_APPROVED",`Chair selected ${AGENTS.find(a=>a.id===agentId)?.label} as lead`,true);
    if(viewMode==="VIEW") grantView(agentId);
  },[addAudit,grantView,viewMode]);

  const connectOAuth = useCallback((agentId)=>{
    const agent=AGENTS.find(a=>a.id===agentId);
    setConnectedAgents(p=>({...p,[agentId]:{connected:true,account:`user@${agent.provider.toLowerCase()}.com`,method:"OAuth"}}));
    addAudit("AGENT_CONNECTED",`${agent.label} connected via ${agent.provider} OAuth`,true);
  },[addAudit]);

  const disconnectAgent = useCallback((agentId)=>{
    setConnectedAgents(p=>{const n={...p};delete n[agentId];return n;});
    addAudit("AGENT_DISCONNECTED",`${AGENTS.find(a=>a.id===agentId)?.label} disconnected`);
  },[addAudit]);

  // S-096-STATE
  const [conversationHistory, setConversationHistory] = React.useState([]);
  const [sessionHistory, setSessionHistory]       = React.useState([]);
  const [savedSessions, setSavedSessions]          = React.useState([]);
  const [historyLoaded, setHistoryLoaded]          = React.useState(false);
  const [stage2Output, setStage2Output]            = React.useState(null);
  const [stage2Loading, setStage2Loading]          = React.useState(false);
  const [lockedScript, setLockedScript]            = React.useState("");
  const [saveSessionName, setSaveSessionName]      = React.useState("");
  const [showSaveModal, setShowSaveModal]          = React.useState(false);
  const conversationEndRef = React.useRef(null);

  const submitTask = useCallback(async()=>{
    if(!task.trim()||halted) return;
    const t=task.trim();
    setCurrentTask(t);setTask("");
    // GOV-NEW-004 — lock governance mode before every task submit
    // S-102: 401 on expired token must clear session — not silently continue
    try{
      const govRes = await fetch(`${API_BASE}/api/governance/mode`,{
        method:"POST",
        headers:{"Content-Type":"application/json","X-Requested-With":"XMLHttpRequest","Authorization":`Bearer ${authToken}`},
        body:JSON.stringify({mode:govMode||"HARD"})
      });
      if(govRes.status===401){
        localStorage.removeItem("uandai_token");
        localStorage.removeItem("uandai_user");
        setAuthToken("");
        setAuthUser(null);
        alert("Your session has expired (15-minute security window). Please sign in again.");
        return;
      }
    }catch(e){}
    setApprovedAgent(null);setShowOpenBid(false);
    const primary=primaryOverride||(userMode==="creative"?CREATIVE_TYPE_PRIMARY[taskType]:TASK_TYPE_PRIMARY[taskType])||"claude";
    let frameAtSubmit=null;
    if(isSharing){frameAtSubmit=captureFrame()||screenFrame;}
    addAudit("TASK_SUBMITTED",`[${taskType}] [${project}] ${t.slice(0,60)}${t.length>60?"…":""}`,true);
    addAudit("GOV_MODE",`${govMode} · Primary: ${AGENTS.find(a=>a.id===primary)?.label}`);
    if(frameAtSubmit) addAudit("SCREEN_FRAME_ATTACHED","Screen frame sent to Claude · ChatGPT · Gemini",true);
    setStatuses(Object.fromEntries(AGENTS.map(a=>[a.id,STATUS.RESPONDING])));
    setResponses(Object.fromEntries(AGENTS.map(a=>[a.id,""])));
    if(viewMode==="VIEW") grantView(primary);
    try{
      // Build roles based on task mode (Custom Project) or clean roles (named projects)
      let finalRoles={};
      // Creative mode — inject creative roles
      if(userMode==="creative"){
        finalRoles = Object.fromEntries(Object.entries(CREATIVE_ROLES).map(([k,v])=>[k,v]));
      } else
      if(isCustom && taskMode && taskMode!=="custom" && TASK_MODE_ROLES[taskMode]){
        finalRoles=TASK_MODE_ROLES[taskMode];
      } else if(isCustom && taskMode==="custom" && customModeText.trim()){
        // Custom instruction — apply same text to all agents
        finalRoles=Object.fromEntries(AGENTS.map(a=>[a.id,customModeText.trim()]));
      } else {
        // No mode — clean roles
        finalRoles=Object.fromEntries(Object.entries(agentRoles).map(([k,v])=>[k,v===AGENTS.find(a=>a.id===k)?.label?"":v]));
      }
      // Append file and github context to task if present
      let taskWithContext = t;
      if(uploadedFile) taskWithContext += `\n\n---\nATTACHED FILE: ${uploadedFile.name}\n${uploadedFile.text}\n---`;
      if(githubContent) taskWithContext += `\n\n---\nGITHUB FILE: ${githubContent.repo}/${githubContent.filepath}\n${githubContent.content}\n---`;
      const taskPayload = userMode==="creative" ? taskWithContext : `[Project: ${project}] [Type: ${taskType}] ${taskWithContext}`;
      const body={task:taskPayload,session_id:sessionId,primary_agent:primary,agent_roles:finalRoles,task_type:taskType,creative_freedom:creativeFreedom,user_mode:userMode};
      if(frameAtSubmit){body.screen_frame=frameAtSubmit;body.screen_frame_note="Chair is sharing their screen. Analyze what you see and apply it to the task.";}
      const res=await fetch(`${API_BASE}/api/task/submit`,{
        method:"POST",
        headers:{"Content-Type":"application/json","X-Requested-With":"XMLHttpRequest","Authorization":`Bearer ${authToken}`},
        body:JSON.stringify(body),
      });
      if(!res.ok){
        if(res.status===401){
          handleLogout();
          alert('Your session has expired. Please log in again.');
          return;
        }
        const err=await res.json().catch(()=>({}));
        if(err.upgrade){
          setStatuses(Object.fromEntries(AGENTS.map(a=>[a.id,STATUS.IDLE])));
          setResponses(Object.fromEntries(AGENTS.map(a=>[a.id,`Free tier limit reached — ${err.elapsed_minutes||45} minutes used today.\n\nUpgrade to Individual ($29/month) or Developer ($79/month) for unlimited access.\n\nYour free tier resets tomorrow.`])));
          addAudit("FREE_TIER_LIMIT",`${err.elapsed_minutes||45} minutes used — upgrade required`);
          return;
        }
        const errMsg=err.error||err.reason||`HTTP ${res.status}`;
        setStatuses(Object.fromEntries(AGENTS.map(a=>[a.id,STATUS.BLOCKED])));
        setResponses(Object.fromEntries(AGENTS.map(a=>[a.id,`Error: ${errMsg}`])));
        addAudit("SUBMIT_ERROR",errMsg);
        return;
      }
      const data=await res.json();
      // Runway video generation — fires automatically on VIDEO task type in creative mode
      if(userMode==="creative" && taskType==="VIDEO" && creativeFreedom){
        setRunwayGenerating(true);
        try{
          const rvRes = await fetch(`${API_BASE}/api/creative/generate-video`,{
            method:"POST",
            headers:{"Content-Type":"application/json","Authorization":`Bearer ${authToken}`,"X-Requested-With":"XMLHttpRequest"},
            body:JSON.stringify({prompt:`${creativeFreedom} ${t}`,duration:10,session_id:sessionId})
          });
          if(rvRes.ok){
            const rvData = await rvRes.json();
            const taskId = rvData.task_id||rvData.id||"";
            if(taskId){
              // Poll for video status
              const poll = setInterval(async()=>{
                try{
                  const stRes = await fetch(`${API_BASE}/api/creative/video-status?task_id=${taskId}`,{
                    headers:{"Authorization":`Bearer ${authToken}`}
                  });
                  if(stRes.ok){
                    const stData = await stRes.json();
                    if(stData.status==="SUCCEEDED"||stData.output_url){
                      clearInterval(poll);
                      setRunwayVideoUrl(stData.output_url||"");
                      setRunwayGenerating(false);
                    }
                  }
                }catch(e){clearInterval(poll);setRunwayGenerating(false);}
              },3000);
              setTimeout(()=>{clearInterval(poll);setRunwayGenerating(false);},120000);
            }
          }
        }catch(e){setRunwayGenerating(false);}
      }

      // ── IRREVERSIBILITY GATE ──────────────────────────────────────────────
      if(data.status==="HARD_LOCK"){
        setHardLock(data);
        setPendingTask({task:t,taskType,project,primary,finalRoles:body.agent_roles,frameAtSubmit});
        setStatuses(Object.fromEntries(AGENTS.map(a=>[a.id,STATUS.IDLE])));
        addAudit("CLASS2_HARD_LOCK",`Class 2 — Irreversible — Chair approval required. Signals: ${(data.signals||[]).slice(0,2).join(", ")}`,true);
        return;
      }
      if(data.chair_notice){
        setChairNotice(data.chair_notice);
        addAudit("CLASS1_NOTICE",`Class 1 — Real-world effect detected. ${data.chair_notice.reason}`,true);
      }
      // ── End irreversibility gate ──────────────────────────────────────────

      const agentList=data.agent_responses||(Array.isArray(data)?data:[]);
      const newResponses = {};
      agentList.forEach(item=>{
        const key=item.agent_key;
        if(!key) return;
        const blocked=item.response&&item.response.startsWith("[GOV-001 BLOCKED]");
        newResponses[key] = item.response||"";
        setStatuses(p=>({...p,[key]:blocked?STATUS.BLOCKED:STATUS.COMPLETE}));
        setResponses(p=>({...p,[key]:
          p[key]
            ? p[key] + "\n\n─────────────────────\n\n" + (item.response||"")
            : (item.response||"")
        }));
        addAudit(blocked?"GOV001_BLOCK":"AGENT_COMPLETE",`${item.agent} ${blocked?"blocked":"responded"}`);
      });
      // Save to Supabase for all user projects
      if(project && project !== "Custom Project"){
        const uid = authUser?.id || "guest";
        // Update in-memory cache
        projectStateRef.current[project] = {
          responses: newResponses,
          currentTask: t,
          agentRoles: agentRoles,
          approvedAgent: null,
        };
        // Persist to Supabase in background
        fetch(`${API_BASE}/api/project/save`,{
          method:"POST",
          headers:{"Content-Type":"application/json","X-Requested-With":"XMLHttpRequest","Authorization":`Bearer ${authToken}`},
          body:JSON.stringify({
            user_id: uid,
            project_name: project,
            last_task: t,
            agent_roles: agentRoles,
            last_responses: newResponses,
            approved_agent: "",
          }),
        }).catch(()=>{}); // Non-blocking — never fail the UI for a save error
      }
      setShowOpenBid(true);
      // Auto-assemble Production Package after SCRIPT task in creative mode
      if(userMode==="creative" && taskType==="SCRIPT" && Object.keys(newResponses).length > 0){
        try{
          const pkgRes = await fetch(`${API_BASE}/api/creative/production-package`,{
            method:"POST",
            headers:{"Content-Type":"application/json","X-Requested-With":"XMLHttpRequest","Authorization":`Bearer ${authToken}`},
            body:JSON.stringify({
              agent_responses: newResponses,
              creative_freedom: creativeFreedom,
              session_id: sessionId,
              task: t,
            })
          });
          if(pkgRes.ok){
            const pkgData = await pkgRes.json();
            if(pkgData.items){
              setProductionPackage({...pkgData, krafts: Object.fromEntries(pkgData.items.map((it,i)=>[`kraft_0${i+1}_${it.craft.toLowerCase().replace(/ /g,"_")}`,it]))});
              setShowProductionPanel(true);
              setPkgEdits({});
              addAudit("PRODUCTION_PACKAGE_READY","RESH CREATIVE STUDIO™ — Package assembled. Chair review required.",true);
            }
          }
        }catch(e){ console.error("Production Package error:",e); }
      }
    } catch(e) {
      setStatuses(Object.fromEntries(AGENTS.map(a=>[a.id,STATUS.BLOCKED])));
      setResponses(Object.fromEntries(AGENTS.map(a=>[a.id,`Connection error: ${e.message}`])));
      addAudit("SUBMIT_ERROR",e.message);
    }
  },[task,halted,govMode,sessionId,taskType,project,addAudit,grantView,primaryOverride,viewMode,isSharing,screenFrame,captureFrame,authToken]);

  const approveClass2Task = useCallback(async()=>{
    if(!pendingTask) return;
    const {task:t, taskType:tt, project:proj, primary, finalRoles, frameAtSubmit} = pendingTask;
    setHardLock(null); setPendingTask(null);
    addAudit("CLASS2_CHAIR_APPROVED","Chair explicitly approved Class 2 irreversible task. Permanent record logged.",true);
    setStatuses(Object.fromEntries(AGENTS.map(a=>[a.id,STATUS.RESPONDING])));
    setResponses(Object.fromEntries(AGENTS.map(a=>[a.id,""])));
    try{
      const body={task:`[Project: ${proj}] [Type: ${tt}] ${t}`,session_id:sessionId,primary_agent:primary,agent_roles:finalRoles,chair_approval:"APPROVE"};
      if(frameAtSubmit){body.screen_frame=frameAtSubmit;}
      const res=await fetch(`${API_BASE}/api/task/approve`,{
        method:"POST",
        headers:{"Content-Type":"application/json","X-Requested-With":"UandAIClient","Authorization":`Bearer ${authToken}`},
        body:JSON.stringify(body),
      });
      const data=await res.json();
      // Runway video generation — fires automatically on VIDEO task type in creative mode
      if(userMode==="creative" && taskType==="VIDEO" && creativeFreedom){
        setRunwayGenerating(true);
        try{
          const rvRes = await fetch(`${API_BASE}/api/creative/generate-video`,{
            method:"POST",
            headers:{"Content-Type":"application/json","Authorization":`Bearer ${authToken}`,"X-Requested-With":"XMLHttpRequest"},
            body:JSON.stringify({prompt:`${creativeFreedom} ${t}`,duration:10,session_id:sessionId})
          });
          if(rvRes.ok){
            const rvData = await rvRes.json();
            const taskId = rvData.task_id||rvData.id||"";
            if(taskId){
              // Poll for video status
              const poll = setInterval(async()=>{
                try{
                  const stRes = await fetch(`${API_BASE}/api/creative/video-status?task_id=${taskId}`,{
                    headers:{"Authorization":`Bearer ${authToken}`}
                  });
                  if(stRes.ok){
                    const stData = await stRes.json();
                    if(stData.status==="SUCCEEDED"||stData.output_url){
                      clearInterval(poll);
                      setRunwayVideoUrl(stData.output_url||"");
                      setRunwayGenerating(false);
                    }
                  }
                }catch(e){clearInterval(poll);setRunwayGenerating(false);}
              },3000);
              setTimeout(()=>{clearInterval(poll);setRunwayGenerating(false);},120000);
            }
          }
        }catch(e){setRunwayGenerating(false);}
      }
      const agentList=data.agent_responses||(Array.isArray(data)?data:[]);
      agentList.forEach(item=>{
        if(!item.agent_key) return;
        setStatuses(p=>({...p,[item.agent_key]:STATUS.COMPLETE}));
        setResponses(p=>({...p,[item.agent_key]:
          p[item.agent_key]
            ? p[item.agent_key] + "\n\n─────────────────────\n\n" + (item.response||"")
            : (item.response||"")
        }));
        addAudit("AGENT_COMPLETE",`${item.agent} responded after Class 2 approval`);
      });
      setShowOpenBid(true);
    } catch(e) {
      setStatuses(Object.fromEntries(AGENTS.map(a=>[a.id,STATUS.BLOCKED])));
      addAudit("SUBMIT_ERROR",e.message);
    }
  },[pendingTask,sessionId,authToken,addAudit]);

  const haltAll=useCallback(()=>{
    setHalted(true);
    stopScreenShare();
    setStatuses(Object.fromEntries(AGENTS.map(a=>[a.id,STATUS.BLOCKED])));
    addAudit("HALT_ALL","Chair issued HALT ALL AI",true);
  },[addAudit,stopScreenShare]);

  // Re-run a single Data Mind with its current role assignment
  const rerunSingleAgent = useCallback(async(agentId)=>{
    if(!currentTask||halted) return;
    const agent = AGENTS.find(a=>a.id===agentId);
    if(!agent) return;
    // Save current response before overwriting
    setPrevResponses(p=>({...p,[agentId]:responses[agentId]}));
    setShowCompare(p=>({...p,[agentId]:true}));
    setStatuses(p=>({...p,[agentId]:STATUS.RESPONDING}));
    setRerunAgent(agentId);
    addAudit("ROLE_RERUN",`Chair re-running ${agent.label} as ${agentRoles[agentId]}`,true);
    try{
      const body={
        task: currentTask,
        session_id: sessionId,
        primary_agent: agentId,
        agent_roles: {[agentId]: agentRoles[agentId]},
        single_agent: agentId,
      };
      const res = await fetch(`${API_BASE}/api/task/submit`,{
        method:"POST",
        headers:{"Content-Type":"application/json","X-Requested-With":"UandAIClient","Authorization":`Bearer ${authToken}`},
        body:JSON.stringify(body),
      });
      if(!res.ok){
        if(res.status===401){
          handleLogout();
          alert('Your session has expired. Please log in again.');
          return;
        }
        throw new Error(`HTTP ${res.status}`);
      }
      const data = await res.json();
      const agentList = data.agent_responses||(Array.isArray(data)?data:[]);
      const found = agentList.find(item=>item.agent_key===agentId);
      if(found){
        setResponses(p=>({...p,[agentId]:found.response||""}));
        setStatuses(p=>({...p,[agentId]:STATUS.COMPLETE}));
        addAudit("RERUN_COMPLETE",`${agent.label} responded under role: ${agentRoles[agentId]}`);
      } else {
        // Fallback — use full response list
        agentList.forEach(item=>{
          if(item.agent_key===agentId){
            setResponses(p=>({...p,[agentId]:item.response||""}));
            setStatuses(p=>({...p,[agentId]:STATUS.COMPLETE}));
          }
        });
      }
    } catch(e) {
      setStatuses(p=>({...p,[agentId]:STATUS.BLOCKED}));
      addAudit("RERUN_ERROR",`${agent.label}: ${e.message}`);
    }
    setRerunAgent(null);
  },[currentTask,halted,responses,agentRoles,sessionId,authToken,addAudit]);

  // S-098 — Stage 2 Storyboard handler
  const handleStage2Storyboard = React.useCallback(async (scriptText) => {
    if (!scriptText || !scriptText.trim()) {
      alert("No locked script. Approve a script in Stage 1 first.");
      return;
    }
    setStage2Loading(true);
    setStage2Output(null);
    try {
      const res = await fetch(API_BASE + "/api/stage2/storyboard", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": "Bearer " + authToken },
        body: JSON.stringify({
          script: scriptText,
          session_id: sessionId,
          creative_freedom: creativeFreedom || "",
          session_name: "Storyboard " + new Date().toLocaleString()
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Stage 2 failed");
      setStage2Output(data);
      addAudit("STAGE2_STORYBOARD", `Stage 2 complete. ${data.agents_completed}/6 agents responded.`, true);
    } catch(e) {
      addAudit("STAGE2_ERROR", e.message);
      alert("Stage 2 error: " + e.message);
    } finally {
      setStage2Loading(false);
    }
  }, [authToken, sessionId, creativeFreedom, addAudit]);

  const handleSaveSession = React.useCallback(async () => {
    if (!saveSessionName.trim()) return;
    try {
      const res = await fetch(API_BASE + "/api/sessions/save", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": "Bearer " + authToken },
        body: JSON.stringify({
          session_name: saveSessionName, task_type: taskType, mode: userMode,
          script_content: lockedScript,
          storyboard_content: stage2Output ? stage2Output.storyboard_outputs : {},
          agent_responses: responses || {},
          production_approved: showProductionPanel || false
        })
      });
      const data = await res.json();
      if (data.saved) {
        setSavedSessions(prev => [{session_name:saveSessionName, created_at:new Date().toISOString(), id:data.session_id}, ...prev]);
        setShowSaveModal(false);
        setSaveSessionName("");
        addAudit("SESSION_SAVED", `Chair saved session: ${saveSessionName}`, true);
      }
    } catch(e) { alert("Save failed: " + e.message); }
  }, [saveSessionName, authToken, taskType, userMode, lockedScript, stage2Output, responses, showProductionPanel, addAudit]);


  const viewData  = viewAgent?AGENTS.find(a=>a.id===viewAgent):null;
  const anyComplete=AGENTS.some(a=>statuses[a.id]===STATUS.COMPLETE);
  const connectedCount=Object.keys(connectedAgents).length;

  // Role display — only show roles for Custom Project
  const showRoles = isCustom;
  const getAgentLabel = (agent) => agent.label;
  const getAgentSubLabel = (agent) => {
    if(isCustom) return agentRoles[agent.id]===agent.label?"":agentRoles[agent.id];
    return agent.provider;
  };

  const [isMobile, setIsMobile] = React.useState(window.innerWidth <= 768);
  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (!authUser && showHero) return <HeroPage user={authUser} onGetStarted={()=>setShowHero(false)} />;
  if (!authUser) return <LoginScreen onLogin={handleLogin} />;

  const T = theme;

  // S-096-FUNCTIONS
  const loadConversationHistory = async (token) => {
    try {
      const res = await fetch(API_BASE + "/api/conversation/history?limit=100", {
        headers: { "Authorization": "Bearer " + token, "X-Requested-With": "XMLHttpRequest" }
      });
      if (res.ok) {
        const data = await res.json();
        if (data.history && data.history.length > 0) {
          setConversationHistory(data.history.map(function(item) {
            return { id: item.id, task: item.task, task_type: item.task_type,
              project: item.project, created_at: item.created_at, responses: item.responses || [] };
          }));
        }
      }
    } catch(e) { console.log("[History]", e); }
    setHistoryLoaded(true);
  };

  const handleClearHistory = async () => {
    if (!window.confirm("Clear all conversation history? Cannot be undone.")) return;
    try {
      await fetch(API_BASE + "/api/conversation/clear", {
        method: "DELETE",
        headers: { "Authorization": "Bearer " + authToken, "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
        body: JSON.stringify({ project: "" })
      });
      setConversationHistory([]);
    } catch(e) { console.log("[Clear]", e); }
  };

  return (
    <div style={{height:"100vh",background:T.bg,color:T.text,fontFamily:"'Inter','Segoe UI',sans-serif",display:"flex",flexDirection:"column",overflow:"hidden",transition:"background 0.2s,color 0.2s"}}>


      {/* ── CLASS 2 HARD LOCK GATE ── */}
      {hardLock&&(
        <div style={{position:"fixed",top:0,left:0,right:0,bottom:0,background:"rgba(0,0,0,0.85)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center"}}>
          <div style={{background:"#1a0000",border:"2px solid #8B0000",borderRadius:"12px",padding:"36px 40px",maxWidth:"520px",width:"90%",textAlign:"center"}}>
            <div style={{fontSize:"48px",marginBottom:"12px"}}><Icon name="dot" size={40} style={{color:"#e53e3e"}}/></div>
            <div style={{fontSize:"22px",fontWeight:"800",color:"#ff4444",marginBottom:"8px",letterSpacing:"2px"}}>CLASS 2 — HARD LOCK</div>
            <div style={{fontSize:"13px",color:"#ffaaaa",marginBottom:"16px",fontWeight:"600"}}>IRREVERSIBLE ACTION DETECTED</div>
            <div style={{fontSize:"13px",color:"#ffcccc",marginBottom:"8px",lineHeight:"1.6"}}>{hardLock.reason}</div>
            <div style={{fontSize:"11px",color:"#ff8888",marginBottom:"24px",fontFamily:"monospace",background:"rgba(139,0,0,0.3)",padding:"8px 12px",borderRadius:"6px"}}>
              Signal: {(hardLock.signals||[]).slice(0,3).join(" · ")}
            </div>
            <div style={{fontSize:"13px",color:"#ffdddd",marginBottom:"24px",lineHeight:"1.6",borderTop:"1px solid #8B0000",paddingTop:"16px"}}>
              No agent has received this task.<br/>
              Chair explicit approval required to proceed.<br/>
              This decision will be permanently logged.
            </div>
            <div style={{display:"flex",gap:"12px",justifyContent:"center"}}>
              <button onClick={()=>{setHardLock(null);setPendingTask(null);addAudit("CLASS2_CANCELLED","Chair cancelled Class 2 task.",true);}}
                style={{padding:"10px 24px",background:"transparent",border:"1px solid #8B0000",color:"#ff6666",borderRadius:"6px",cursor:"pointer",fontSize:"13px",fontWeight:"600"}}>
                <Icon name="x" size={13}/> Cancel Task
              </button>
              <button onClick={approveClass2Task}
                style={{padding:"10px 28px",background:"#8B0000",border:"none",color:"#fff",borderRadius:"6px",cursor:"pointer",fontSize:"14px",fontWeight:"800",letterSpacing:"1px"}}>
                <Icon name="bolt" size={14}/> APPROVE — I Understand This Is Irreversible
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── CLASS 1 CHAIR NOTICE ── */}
      {chairNotice&&(
        <div style={{position:"fixed",top:"70px",left:"50%",transform:"translateX(-50%)",zIndex:8888,background:"#1a1400",border:"2px solid #C9A84C",borderRadius:"10px",padding:"16px 24px",maxWidth:"480px",width:"90%",display:"flex",alignItems:"flex-start",gap:"12px",boxShadow:"0 4px 24px rgba(201,168,76,0.3)"}}>
          <div style={{fontSize:"24px",flexShrink:0}}><Icon name="warning" size={22}/></div>
          <div style={{flex:1}}>
            <div style={{fontSize:"13px",fontWeight:"700",color:"#C9A84C",marginBottom:"4px"}}>CLASS 1 — REAL-WORLD EFFECT</div>
            <div style={{fontSize:"12px",color:"#ffe0a0",lineHeight:"1.5"}}>{chairNotice.reason}</div>
            <div style={{fontSize:"11px",color:"#C9A84C",marginTop:"4px"}}>Agents are dispatching. Use HALT ALL AI to stop.</div>
          </div>
          <button onClick={()=>setChairNotice(null)} style={{background:"transparent",border:"none",color:"#C9A84C",cursor:"pointer",fontSize:"18px",flexShrink:0}}><Icon name="x" size={16}/></button>
        </div>
      )}

      {/* HEADER — S-097 compact icon strip — S-101 mobile responsive */}
      <div style={{background:T.bg2,borderBottom:`1px solid ${T.border2}`,padding:"5px 14px",display:"flex",alignItems:"center",justifyContent:"space-between",flexShrink:0,minHeight:"42px",flexWrap:"wrap",gap:"6px",overflowX:"hidden"}}>
        <div style={{display:"flex",alignItems:"center",gap:"12px"}}>
          <div>
            <span style={{fontSize:"16px",fontWeight:"700",color:T.gold}}>UandAI™</span>
            {userMode==="creative"&&<span style={{fontSize:"9px",color:T.gold,opacity:0.7,marginLeft:"8px",letterSpacing:"1px"}}>RESH CREATÏVE STUDIO™</span>}
          </div>
          <div style={{display:"flex",alignItems:"center",gap:"3px"}}>
            <span title="SEE — D.A.V.I.D. Forensic Spine" style={{width:"26px",height:"26px",borderRadius:"5px",background:T.bg3,border:`1px solid ${T.border2}`,display:"inline-flex",alignItems:"center",justifyContent:"center",fontSize:"10px",color:T.gold,fontWeight:"700",cursor:"default"}}>S</span>
            <button onClick={()=>{setSpineOpen(true);setSpineTab("AUDIT");}} title="AUDIT — Audit log"
              style={{width:"26px",height:"26px",borderRadius:"5px",background:T.bg3,border:`1px solid ${T.border2}`,display:"inline-flex",alignItems:"center",justifyContent:"center",fontSize:"10px",color:T.gold,fontWeight:"700",cursor:"pointer",padding:0}}>A</button>
            <button onClick={()=>{setSpineOpen(true);setSpineTab("VERIFY");}} title="VERIFY — Cryptographic proof"
              style={{width:"26px",height:"26px",borderRadius:"5px",background:T.bg3,border:`1px solid ${T.border2}`,display:"inline-flex",alignItems:"center",justifyContent:"center",fontSize:"10px",color:T.gold,fontWeight:"700",cursor:"pointer",padding:0}}>V</button>
          </div>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:"8px"}}>
          <span style={{fontSize:"9px",color:T.textDim,maxWidth:isMobile?"70px":"120px",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",display:"inline-block"}}>{sessionId}</span>
          {/* View / Board toggle */}
          <div style={{display:"flex",background:T.bg3,borderRadius:"4px",border:`1px solid ${T.border2}`,overflow:"hidden"}}>
            {[["VIEW","▶ View"],["BOARD","⊞ Board"]].map(([mode,label])=>(
              <button key={mode} onClick={()=>setViewMode(mode)}
                style={{padding:"4px 12px",border:"none",background:viewMode===mode?T.gold+"22":"transparent",color:viewMode===mode?T.gold:T.textMuted,cursor:"pointer",fontSize:"10px",fontWeight:"700",letterSpacing:"0.5px"}}>
                {label}
              </button>
            ))}
          </div>
          {/* Light/Dark toggle — named projects only */}
          {canToggleDark&&(
            <button onClick={()=>setDarkMode(d=>!d)}
              style={{background:"transparent",border:`1px solid ${T.border2}`,color:T.gold,padding:"4px 10px",borderRadius:"4px",cursor:"pointer",fontSize:"10px",fontWeight:"700"}}>
              {darkMode?<><Icon name="sun" size={13}/> Light</>:<><Icon name="moon" size={13}/> Dark</>}
            </button>
          )}
          <button onClick={isSharing?stopScreenShare:startScreenShare}
            style={{background:isSharing?T.bg3:"transparent",border:`1px solid ${isSharing?"#48bb78":T.border2}`,color:isSharing?"#48bb78":T.gold,padding:"4px 12px",borderRadius:"4px",cursor:"pointer",fontSize:"10px",fontWeight:"700"}}>
            {isSharing?<><Icon name="screen" size={14}/> Sharing…</>:<><Icon name="screen" size={14}/> Share Screen</>}
          </button>
          <button onClick={()=>setShowConnect(p=>!p)}
            style={{background:showConnect?T.gold+"22":"transparent",border:`1px solid ${connectedCount>0?"#48bb78":T.border2}`,color:connectedCount>0?"#48bb78":T.gold,padding:"4px 12px",borderRadius:"4px",cursor:"pointer",fontSize:"10px",fontWeight:"700"}}>
            {connectedCount>0?`${connectedCount} Connected`:"Connect Agents"}
          </button>
          <button onClick={()=>{const n=govMode==="HARD"?"SOFT":"HARD";setGovMode(n);addAudit("MODE_SWITCH",n);}}
            style={{background:T.bg2,border:`1px solid ${T.gold}`,color:T.gold,padding:"5px 12px",borderRadius:"4px",cursor:"pointer",fontSize:"11px",fontWeight:"700",letterSpacing:"1px"}}>
            {govMode} MODE
          </button>
          <button onClick={haltAll}
            style={{background:"#7f0000",border:"none",color:"#fff",padding:"6px 14px",borderRadius:"4px",cursor:"pointer",fontSize:"11px",fontWeight:"700",letterSpacing:"1px"}}>
            <Icon name="block" size={14}/> STOP
          </button>
          <button onClick={()=>{const nm=userMode==="developer"?"creative":"developer";setUserMode(nm);setTaskType(nm==="creative"?"SCRIPT":"SPEC");addAudit("MODE_SWITCH","Switched to "+nm+" mode");}}
            style={{background:userMode==="creative"?"#7B2D8B22":"transparent",border:`1px solid ${userMode==="creative"?"#7B2D8B":T.border2}`,color:userMode==="creative"?"#c084fc":T.gold,padding:"5px 12px",borderRadius:"4px",cursor:"pointer",fontSize:"10px",fontWeight:"700",letterSpacing:"0.5px"}}>
            {userMode==="creative"?<><Icon name="clapper" size={14}/> Creative</>:<><Icon name="tools" size={14}/> Developer</>}
          </button>
          {!isMobile && <button onClick={()=>setShowHelp(true)}
            style={{background:'transparent',border:`1px solid ${T.border}`,color:T.textDim,padding:'6px 12px',borderRadius:'4px',cursor:'pointer',fontSize:'11px',marginLeft:'6px'}}>
            ? Help
          </button>}
        </div>
      </div>

      {/* SCREEN SHARE BAR */}
      {isSharing&&(
        <div style={{background:"#0a2018",borderBottom:"1px solid #48bb7833",padding:"5px 20px",display:"flex",alignItems:"center",gap:"12px",flexShrink:0}}>
          <span style={{fontSize:"10px",color:"#48bb78",fontWeight:"700"}}><Icon name="screen" size={12}/> SCREEN SHARE ACTIVE</span>
          <span style={{fontSize:"10px",color:T.textMuted}}>Claude · ChatGPT · Gemini see your screen on every submit</span>
          <span style={{fontSize:"10px",color:T.textDim}}>Grok · Copilot · Perplexity — text only</span>
          {screenFrame&&<span style={{fontSize:"9px",color:"#48bb78",marginLeft:"auto",background:"#0f3020",border:"1px solid #48bb7833",padding:"2px 8px",borderRadius:"3px"}}><Icon name="check" size={11}/> Frame ready</span>}
          <button onClick={stopScreenShare} style={{fontSize:"9px",padding:"2px 10px",borderRadius:"3px",border:"1px solid #e53e3e44",background:"#1a0808",color:"#e53e3e",cursor:"pointer",fontWeight:"700"}}>Stop</button>
        </div>
      )}
      {screenError&&(
        <div style={{background:"#1a0808",borderBottom:"1px solid #e53e3e44",padding:"5px 20px",display:"flex",alignItems:"center",gap:"10px",flexShrink:0}}>
          <span style={{fontSize:"10px",color:"#e53e3e",fontWeight:"700"}}><Icon name="warning" size={12}/> {screenError}</span>
          <button onClick={()=>setScreenError("")} style={{fontSize:"9px",color:T.textDim,background:"transparent",border:"none",cursor:"pointer",marginLeft:"auto"}}><Icon name="x" size={12}/></button>
        </div>
      )}

      {/* CHAIR BAR */}
      <div style={{background:T.bg3,borderBottom:`1px solid ${T.border}`,padding:"6px 12px",display:"flex",alignItems:"center",gap:"8px",flexShrink:0,flexWrap:"wrap"}}>
        <span style={{fontSize:"10px",color:T.textMuted,fontWeight:"700",letterSpacing:"1px"}}>CHAIR</span>
        <span style={{fontSize:"13px",color:T.gold,fontWeight:"700"}}>{authUser?.full_name || "Vijay Anand Raj Kanaparthy"}</span>
        <span style={{fontSize:"10px",color:"#48bb78",fontWeight:"600",background:"#0f2010",border:"1px solid #48bb7844",padding:"2px 8px",borderRadius:"3px"}}><Icon name="check" size={11}/> VERIFIED</span>
        <span style={{fontSize:"11px",color:T.gold,background:T.gold+"11",border:`1px solid ${T.gold}33`,padding:"2px 10px",borderRadius:"3px",fontWeight:"600"}}><Icon name="folder" size={11}/> {project}</span>
        <button onClick={handleLogout}
          style={{marginLeft:"auto",fontSize:"9px",padding:"3px 10px",borderRadius:"3px",border:`1px solid ${T.border}`,background:"transparent",color:T.textMuted,cursor:"pointer"}}>
          Sign Out
        </button>
        {!isMobile&&<span style={{fontSize:"10px",color:T.textDim}}>John 14:26 · {govMode} governance · 6 agents ready</span>}
      </div>

      <HelpModal/>
      {halted&&<div style={{background:"#7f0000",color:"#fff",textAlign:"center",padding:"6px",fontSize:"11px",fontWeight:"700",flexShrink:0}}><Icon name="block" size={12}/> ALL AI HALTED · Refresh to resume</div>}

      {approvedAgent&&(
        <div style={{background:"#0f2010",borderBottom:"1px solid #48bb7844",padding:"6px 20px",display:"flex",alignItems:"center",gap:"10px",flexShrink:0}}>
          <span style={{fontSize:"11px",color:"#48bb78",fontWeight:"700"}}><Icon name="check" size={11}/> CHAIR APPROVED</span>
          <span style={{fontSize:"11px",color:"#48bb78"}}>{AGENTS.find(a=>a.id===approvedAgent)?.label} selected as lead</span>
          <span style={{fontSize:"9px",color:T.textDim,marginLeft:"auto"}}>Logged to Forensic Spine · John 14:26</span>
        </div>
      )}

      {/* CONNECT PANEL */}
      {showConnect&&(
        <div style={{background:T.bg3,borderBottom:`1px solid ${T.border2}`,padding:"12px 20px",flexShrink:0}}>
          <div style={{display:"flex",gap:"4px",marginBottom:"12px",alignItems:"center"}}>
            <span style={{fontSize:"10px",color:T.gold,fontWeight:"700",letterSpacing:"1.5px",marginRight:"12px"}}>CONNECT YOUR AI AGENTS</span>
            {["CONSUMER","ENTERPRISE"].map(tab=>(
              <button key={tab} onClick={()=>setConnectTab(tab)}
                style={{padding:"4px 14px",borderRadius:"4px",border:connectTab===tab?`1px solid ${T.gold}`:`1px solid ${T.border2}`,background:connectTab===tab?T.gold:"transparent",color:connectTab===tab?"#080D1C":T.gold,cursor:"pointer",fontSize:"10px",fontWeight:"700"}}>
                {tab==="CONSUMER"?"Personal":"Business"}
              </button>
            ))}
          </div>
          {connectTab==="CONSUMER"&&(
            <>
              <div style={{fontSize:"10px",color:T.textMuted,marginBottom:"10px"}}>Sign in with your own account. One click per agent. No API keys. Your account — your quota — your privacy.</div>
              <div style={{display:"flex",gap:"8px",flexWrap:"wrap"}}>
                {AGENTS.map(agent=>{
                  const isConnected=!!connectedAgents[agent.id];
                  return(
                    <div key={agent.id} style={{background:T.cardBg,border:`1px solid ${isConnected?"#48bb7844":T.border}`,borderRadius:"8px",padding:"10px 14px",minWidth:"160px"}}>
                      <div style={{display:"flex",alignItems:"center",gap:"6px",marginBottom:"6px"}}>
                        <div style={{width:"7px",height:"7px",borderRadius:"50%",background:isConnected?"#48bb78":"#4a5568"}}/>
                        <span style={{fontSize:"11px",fontWeight:"700",color:agent.color}}>{agent.label}</span>
                        <span style={{fontSize:"9px",color:T.textDim}}>{agent.provider}</span>
                        {agent.vision&&<span style={{fontSize:"8px",color:"#63b3ed"}}><Icon name="eye" size={11}/></span>}
                      </div>
                      {isConnected?(
                        <>
                          <div style={{fontSize:"9px",color:"#48bb78",marginBottom:"5px"}}><Icon name="check" size={11}/> {connectedAgents[agent.id].account}</div>
                          <button onClick={()=>disconnectAgent(agent.id)} style={{width:"100%",padding:"4px 0",borderRadius:"3px",border:"1px solid #e53e3e44",background:"#1a0808",color:"#e53e3e",cursor:"pointer",fontSize:"9px",fontWeight:"700"}}>Disconnect</button>
                        </>
                      ):agent.oauthAvailable?(
                        <button onClick={()=>connectOAuth(agent.id)} style={{width:"100%",padding:"5px 0",borderRadius:"3px",border:`1px solid ${agent.color}44`,background:`${agent.color}11`,color:agent.color,cursor:"pointer",fontSize:"9px",fontWeight:"700"}}>Sign in with {agent.provider}</button>
                      ):(
                        <div style={{fontSize:"9px",color:T.textDim,fontStyle:"italic"}}>OAuth coming soon</div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
          {connectTab==="ENTERPRISE"&&(
            <>
              <div style={{fontSize:"10px",color:T.textMuted,marginBottom:"10px"}}>Enterprise PAIL — API key per agent. Fernet AES-256 + PBKDF2-SHA256. Air-gap compatible.</div>
              <div style={{display:"flex",gap:"8px",flexWrap:"wrap"}}>
                {AGENTS.map(agent=>(
                  <div key={agent.id} style={{display:"flex",alignItems:"center",gap:"6px",background:T.cardBg,border:`1px solid ${T.border}`,borderRadius:"5px",padding:"7px 10px"}}>
                    <div style={{width:"7px",height:"7px",borderRadius:"50%",background:pailKeys[agent.id]?"#48bb78":"#4a5568",flexShrink:0}}/>
                    <span style={{fontSize:"10px",fontWeight:"700",color:agent.color,minWidth:"70px"}}>{agent.label}</span>
                    <input type="password" placeholder={`${agent.provider} API key`} value={pailKeys[agent.id]} onChange={e=>setPailKeys(p=>({...p,[agent.id]:e.target.value}))}
                      style={{background:T.inputBg,border:`1px solid ${T.border}`,color:T.text,padding:"4px 8px",borderRadius:"3px",fontSize:"10px",width:"170px",outline:"none"}}/>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* MAIN BODY */}
      <div style={{display:"flex",flex:1,overflow:"hidden",minHeight:0,flexDirection:isMobile?"column":"row"}}>

        {/* LEFT */}
        <div style={{width:isMobile?"100%":"210px",minWidth:isMobile?"unset":"210px",borderRight:isMobile?"none":`1px solid ${T.border}`,borderBottom:isMobile?`1px solid ${T.border}`:"none",display:isMobile?"none":"flex",flexDirection:"column",flexShrink:0}}>
          <div style={{display:"flex",borderBottom:`1px solid ${T.border}`,flexShrink:0}}>
            {["PROJECT","AGENTS","HISTORY"].map(tab=>(
              <button key={tab} onClick={()=>setLeftTab(tab)}
                style={{flex:1,padding:"7px 0",fontSize:"9px",fontWeight:"700",letterSpacing:"1px",border:"none",cursor:"pointer",background:leftTab===tab?T.bg3:"transparent",color:leftTab===tab?T.gold:T.textMuted,borderBottom:leftTab===tab?`2px solid ${T.gold}`:"2px solid transparent"}}>
                {tab}
              </button>
            ))}
          </div>
          {leftTab==="PROJECT"&&(
            <div style={{flex:1,overflowY:"auto",padding:"10px"}}>
              <div style={{marginBottom:"14px"}}>
                <div style={{fontSize:"9px",color:T.textMuted,fontWeight:"700",letterSpacing:"1px",marginBottom:"6px"}}>TASK TYPE</div>
                <div style={{display:"flex",flexDirection:"column",gap:"2px"}}>
                  {(userMode==="creative"?["SCRIPT","STORYBOARD","VIDEO","IMAGE","AUDIO","MUSIC"]:["SPEC","CODE","SECURITY","RESEARCH","DEPLOY","REVIEW","DEBUG"]).map(tt=>(
                    <button key={tt} onClick={()=>setTaskType(tt)}
                      style={{padding:"5px 8px",borderRadius:"4px",border:`1px solid ${taskType===tt?T.gold:T.border}`,background:taskType===tt?T.gold+"22":"transparent",color:taskType===tt?T.gold:T.textDim,cursor:"pointer",fontSize:"10px",fontWeight:taskType===tt?"700":"400",textAlign:"left",letterSpacing:"0.5px"}}>
                      {tt}
                    </button>
                  ))}
                </div>
              </div>
              <div style={{fontSize:"9px",color:T.textMuted,fontWeight:"700",letterSpacing:"1px",marginBottom:"6px"}}>ACTIVE PROJECT</div>
              {/* S-073: User-specific projects from API only */}
              {projectsLoading && (
                <div style={{fontSize:"9px",color:T.textMuted,fontStyle:"italic",padding:"4px 0"}}>Loading...</div>
              )}
              {!projectsLoading && userProjects.length === 0 && (
                <div style={{fontSize:"9px",color:T.textMuted,fontStyle:"italic",padding:"4px 0"}}>No projects yet.</div>
              )}
              {!projectsLoading && userProjects.map(p=>(
                <div key={p} style={{display:"flex",alignItems:"center",gap:"3px",marginBottom:"3px"}}>
                  <button onClick={()=>handleProjectChange(p)}
                    style={{textAlign:"left",flex:1,padding:"6px 8px",borderRadius:"4px",
                      border:project===p?`1px solid ${T.gold}`:`1px solid ${T.border}`,
                      background:project===p?T.gold+"22":"transparent",
                      color:project===p?T.gold:T.textMuted,
                      cursor:"pointer",fontSize:"10px",
                      fontWeight:project===p?"700":"400",
                      overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                    {p}
                  </button>
                  {p !== "My Workspace" && (
                    <button onClick={()=>handleDeleteProject(p)}
                      title="Delete project"
                      style={{background:"none",border:"none",
                        color:"rgba(240,237,228,0.25)",fontSize:"13px",
                        cursor:"pointer",padding:"0 2px",lineHeight:1,flexShrink:0}}
                      onMouseEnter={e=>e.target.style.color="#ff6b6b"}
                      onMouseLeave={e=>e.target.style.color="rgba(240,237,228,0.25)"}>
                      ×
                    </button>
                  )}
                </div>
              ))}
              {/* Add new project button */}
              <div style={{marginTop:"8px"}}>
                {showNewProject ? (
                  <div style={{display:"flex",gap:"4px"}}>
                    <input
                      value={newProjectName}
                      onChange={e=>setNewProjectName(e.target.value)}
                      onKeyDown={e=>e.key==="Enter"&&handleAddProject()}
                      placeholder="Project name..."
                      style={{flex:1,padding:"4px 6px",fontSize:"9px",background:T.bg2,border:`1px solid ${T.gold}`,color:T.text,borderRadius:"3px"}}
                      autoFocus
                    />
                    <button onClick={handleAddProject}
                      style={{padding:"4px 6px",fontSize:"9px",background:T.gold,color:T.bg,border:"none",borderRadius:"3px",cursor:"pointer",fontWeight:"700"}}>+</button>
                    <button onClick={()=>setShowNewProject(false)}
                      style={{padding:"4px 6px",fontSize:"9px",background:"transparent",color:T.textMuted,border:`1px solid ${T.border}`,borderRadius:"3px",cursor:"pointer"}}><Icon name="x" size={11}/></button>
                  </div>
                ) : (
                  <button onClick={()=>setShowNewProject(true)}
                    style={{width:"100%",padding:"5px 8px",fontSize:"9px",background:"transparent",border:`1px dashed ${T.gold}44`,color:T.gold+"88",borderRadius:"4px",cursor:"pointer",textAlign:"left"}}>
                    + New Project
                  </button>
                )}
              </div>

              {/* Delete button for custom projects */}
              {customProjects.includes(project) && (
                <button onClick={()=>handleDeleteProject(project)}
                  style={{width:"100%",marginTop:"6px",padding:"4px 8px",fontSize:"9px",background:"transparent",border:"1px solid #ff444444",color:"#ff6666",borderRadius:"4px",cursor:"pointer",textAlign:"left"}}>
                  <Icon name="trash" size={13}/> Delete "{project}"
                </button>
              )}

              {/* File Upload */}
              <div style={{marginTop:"12px"}}>
                <div style={{fontSize:"9px",color:T.textMuted,fontWeight:"700",letterSpacing:"1px",marginBottom:"5px"}}><Icon name="paperclip" size={11}/> ATTACH FILE</div>
                <label style={{display:"block",padding:"5px 8px",fontSize:"9px",background:"transparent",border:`1px dashed ${T.border}`,color:T.textMuted,borderRadius:"4px",cursor:"pointer",textAlign:"center"}}>
                  {uploadedFile ? <><Icon name="checkCircle" size={12}/> {uploadedFile.name}</> : "Click to upload PDF/TXT/DOCX/Code"}
                  <input type="file" style={{display:"none"}} onChange={handleFileUpload}
                    accept=".pdf,.txt,.md,.docx,.py,.js,.csv,.json" />
                </label>
                {uploadedFile && (
                  <div style={{marginTop:"4px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                    <span style={{fontSize:"9px",color:T.textDim}}>{uploadedFile.chars} chars extracted</span>
                    <button onClick={()=>setUploadedFile(null)}
                      style={{fontSize:"9px",background:"transparent",border:"none",color:"#ff6666",cursor:"pointer"}}><Icon name="x" size={11}/> Clear</button>
                  </div>
                )}
              </div>

              {/* GitHub Integration */}
              <div style={{marginTop:"12px"}}>
                <div style={{fontSize:"9px",color:T.textMuted,fontWeight:"700",letterSpacing:"1px",marginBottom:"5px"}}><Icon name="link" size={11}/> GITHUB FILE</div>
                <input value={githubRepo} onChange={e=>setGithubRepo(e.target.value)}
                  placeholder="owner/repo"
                  style={{width:"100%",padding:"4px 6px",fontSize:"9px",background:T.bg2,border:`1px solid ${T.border}`,color:T.text,borderRadius:"3px",marginBottom:"3px",boxSizing:"border-box"}} />
                <input value={githubFile} onChange={e=>setGithubFile(e.target.value)}
                  placeholder="path/to/file.py"
                  style={{width:"100%",padding:"4px 6px",fontSize:"9px",background:T.bg2,border:`1px solid ${T.border}`,color:T.text,borderRadius:"3px",marginBottom:"3px",boxSizing:"border-box"}} />
                <input value={githubToken} onChange={e=>setGithubToken(e.target.value)}
                  placeholder="Token (optional — private repos)"
                  type="password"
                  style={{width:"100%",padding:"4px 6px",fontSize:"9px",background:T.bg2,border:`1px solid ${T.border}`,color:T.text,borderRadius:"3px",marginBottom:"4px",boxSizing:"border-box"}} />
                <button onClick={handleGithubRead}
                  style={{width:"100%",padding:"5px 8px",fontSize:"9px",background:githubRepo&&githubFile?T.gold:"transparent",color:githubRepo&&githubFile?T.bg:T.textMuted,border:`1px solid ${githubRepo&&githubFile?T.gold:T.border}`,borderRadius:"4px",cursor:"pointer",fontWeight:"700"}}>
                  Read File
                </button>
                {githubContent && (
                  <div style={{marginTop:"4px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                    <span style={{fontSize:"9px",color:T.textDim}}><Icon name="checkCircle" size={11}/> {githubContent.filepath} — {githubContent.chars} chars</span>
                    <button onClick={()=>setGithubContent(null)}
                      style={{fontSize:"9px",background:"transparent",border:"none",color:"#ff6666",cursor:"pointer"}}><Icon name="x" size={11}/></button>
                  </div>
                )}
              </div>

              {auditLog.filter(e=>e.event==="TASK_SUBMITTED").length>0&&(
                <div style={{marginTop:"12px"}}>
                  <div style={{fontSize:"9px",color:T.textMuted,fontWeight:"700",letterSpacing:"1px",marginBottom:"5px"}}>SESSION HISTORY</div>
                  {auditLog.filter(e=>e.event==="TASK_SUBMITTED").map(e=>(
                    <div key={e.id} style={{fontSize:"9px",color:T.textDim,padding:"3px 0",borderBottom:`1px solid ${T.border}`,lineHeight:"1.4"}}>
                      <span style={{color:T.gold+"99"}}>{e.time}</span><br/>
                      {e.detail.slice(0,50)}{e.detail.length>50?"…":""}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {leftTab==="HISTORY"&&(
                <div style={{padding:"0 4px"}}>
                  <div style={{fontSize:"9px",color:T.gold,fontWeight:"700",letterSpacing:"2px",marginBottom:"10px",paddingBottom:"4px",borderBottom:`1px solid ${T.border2}`}}>SESSION HISTORY</div>
                  {savedSessions.length>0&&(
                    <div style={{marginBottom:"10px"}}>
                      <div style={{fontSize:"8px",color:T.textMuted,letterSpacing:"1px",marginBottom:"4px"}}>SAVED</div>
                      {savedSessions.slice(0,10).map((s,i)=>(
                        <div key={s.id||i} style={{padding:"5px 8px",marginBottom:"2px",background:T.bg3,borderRadius:"4px",borderLeft:`2px solid ${T.gold}`,cursor:"pointer"}} onClick={()=>addAudit("SESSION_LOAD",s.session_name)}>
                          <div style={{fontSize:"10px",color:T.text,overflow:"hidden",whiteSpace:"nowrap",textOverflow:"ellipsis"}}>{s.session_name}</div>
                          <div style={{fontSize:"8px",color:T.textMuted}}>{s.task_type||"—"} · {new Date(s.created_at).toLocaleDateString()}</div>
                        </div>
                      ))}
                    </div>
                  )}
                  {sessionHistory.length>0&&(
                    <div>
                      <div style={{fontSize:"8px",color:T.textMuted,letterSpacing:"1px",marginBottom:"4px"}}>RECENT TASKS</div>
                      {sessionHistory.slice(0,20).map((t,i)=>(
                        <div key={t.id||i} style={{padding:"5px 8px",marginBottom:"2px",background:T.bg2,borderRadius:"4px"}}>
                          <div style={{fontSize:"10px",color:T.textMuted,overflow:"hidden",whiteSpace:"nowrap",textOverflow:"ellipsis"}}>{t.task_text?t.task_text.slice(0,55):"—"}</div>
                          <div style={{fontSize:"8px",color:"#444"}}>{t.task_type||"—"} · {new Date(t.created_at).toLocaleDateString()}</div>
                        </div>
                      ))}
                    </div>
                  )}
                  {sessionHistory.length===0&&savedSessions.length===0&&(
                    <div style={{fontSize:"10px",color:T.textMuted,padding:"12px 0",fontStyle:"italic",textAlign:"center"}}>No sessions yet.</div>
                  )}
                  {responses&&Object.keys(responses).length>0&&(
                    <button onClick={()=>setShowSaveModal(true)} style={{marginTop:"10px",width:"100%",padding:"6px",background:"transparent",border:`1px solid ${T.gold}`,color:T.gold,borderRadius:"4px",fontSize:"9px",letterSpacing:"1px",cursor:"pointer",fontWeight:"700"}}>+ SAVE THIS SESSION</button>
                  )}
                </div>
              )}
              {leftTab==="AGENTS"&&(
            <div style={{flex:1,overflowY:"auto",padding:"10px"}}>
              {/* Roles panel — Custom Project only */}
              {isCustom?(
                <>
                  <div style={{fontSize:"9px",color:T.textMuted,fontWeight:"700",letterSpacing:"1px",marginBottom:"3px"}}>AGENTS · {taskType}</div>
                  <div style={{fontSize:"9px",color:T.textDim,marginBottom:"8px"}}>Set primary agent. Task mode selected below.</div>
                  {AGENTS.map(agent=>{
                    const isPrimary=(primaryOverride||TASK_TYPE_PRIMARY[taskType])===agent.id;
                    const currentRole=agentRoles[agent.id];
                    const isEditing=editingRole===agent.id;
                    return(
                      <div key={agent.id} style={{padding:"6px 7px",borderRadius:"4px",border:isPrimary?`1px solid ${agent.color}`:`1px solid ${T.border}`,background:isPrimary?`${agent.color}08`:"transparent",marginBottom:"4px"}}>
                        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"3px"}}>
                          <span style={{fontSize:"10px",fontWeight:"700",color:agent.color}}>{agent.label}{agent.vision?<Icon name="eye" size={10} style={{marginLeft:"3px"}}/>:null}</span>
                          <button onClick={()=>{setPrimaryOverride(agent.id);addAudit("PRIMARY_SET",`Chair set ${agent.label} as primary`);}}
                            style={{fontSize:"8px",padding:"1px 5px",borderRadius:"3px",border:isPrimary?`1px solid ${agent.color}`:`1px solid ${T.gold}33`,background:isPrimary?agent.color:"transparent",color:isPrimary?"#080D1C":T.gold,cursor:"pointer",fontWeight:"700"}}>
                            {isPrimary?"PRIMARY":"SET"}
                          </button>
                        </div>
                        {isEditing?(
                          <select value={currentRole}
                            onChange={e=>{setAgentRoles(p=>({...p,[agent.id]:e.target.value}));addAudit("ROLE_ASSIGNED",`${agent.label} → ${e.target.value}`,true);setEditingRole(null);}}
                            onBlur={()=>setEditingRole(null)} autoFocus
                            style={{width:"100%",background:T.inputBg,border:`1px solid ${T.gold}`,color:T.gold,padding:"2px",borderRadius:"3px",fontSize:"9px"}}>
                            <option value={agent.label}>{agent.label} (no role)</option>
                            {(userMode==="creative"?["Screenwriter","Production Designer","Continuity Supervisor","Rights & Clearance","Technical Director","Reference Researcher"]:ALL_ROLES).map(r=><option key={r} value={r}>{r}</option>)}
                          </select>
                        ):(
                          <div onClick={()=>setEditingRole(agent.id)} style={{fontSize:"9px",color:T.gold,cursor:"pointer",display:"inline-block",padding:"1px 4px",borderRadius:"2px",border:`1px solid ${T.gold}22`}}>
                            {currentRole} <Icon name="pencil" size={11}/>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {primaryOverride&&(
                    <button onClick={()=>{setPrimaryOverride(null);addAudit("PRIMARY_RESET","Reset to default routing");}}
                      style={{width:"100%",marginTop:"6px",padding:"4px 0",borderRadius:"3px",border:"1px solid #e53e3e44",background:"#1a0808",color:"#e53e3e",cursor:"pointer",fontSize:"9px",fontWeight:"700"}}>
                      Reset Default Routing
                    </button>
                  )}
                </>
              ):(
                <>
                  {/* Named projects — agent list, no roles */}
                  <div style={{fontSize:"9px",color:T.textMuted,fontWeight:"700",letterSpacing:"1px",marginBottom:"8px"}}>6 AGENTS</div>
                  {AGENTS.map(agent=>{
                    const s=statuses[agent.id];
                    const ss=STATUS_STYLE[s]||STATUS_STYLE.IDLE;
                    return(
                      <div key={agent.id} style={{padding:"6px 7px",borderRadius:"4px",border:`1px solid ${T.border}`,marginBottom:"4px",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                        <div>
                          <span style={{fontSize:"10px",fontWeight:"700",color:agent.color}}>{agent.label}{agent.vision?<Icon name="eye" size={10} style={{marginLeft:"3px"}}/>:null}</span>
                          <div style={{fontSize:"9px",color:T.textDim}}>{agent.provider}</div>
                        </div>
                        <div style={{fontSize:"8px",fontWeight:"700",padding:"1px 5px",borderRadius:"3px",color:ss.color,background:ss.bg,border:`1px solid ${ss.border}`}}>
                          {s===STATUS.RESPONDING?"● ":""}{s}
                        </div>
                      </div>
                    );
                  })}
                </>
              )}
            </div>
          )}
        </div>

        {/* CENTER */}
        <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"auto",minWidth:0}}>


          {/* CREATIVE FREEDOM BAR — only in creative mode */}
          {userMode==="creative"&&(
            <div style={{background:"#080D1C",borderBottom:`1px solid rgba(201,168,76,0.2)`,padding:"8px 16px",display:"flex",alignItems:"center",gap:"10px",flexShrink:0}}>
              <span style={{fontSize:"9px",color:"#C9A84C",fontWeight:"700",letterSpacing:"1px",whiteSpace:"nowrap"}}>CREATIVE FREEDOM</span>
              <input
                value={creativeFreedom}
                onChange={e=>setCreativeFreedom(e.target.value)}
                placeholder="Define your creative world — tone, setting, characters, rules — injected into every agent call..."
                style={{flex:1,background:"rgba(255,255,255,0.05)",border:"0.5px solid rgba(201,168,76,0.2)",borderRadius:"6px",color:"#F0EDE4",fontSize:"11px",padding:"5px 10px",outline:"none",fontFamily:"inherit"}}
              />
              {runwayGenerating&&(
                <span style={{fontSize:"9px",color:"#C9A84C",fontWeight:"700",letterSpacing:"1px",whiteSpace:"nowrap",animation:"pulse 1.5s infinite"}}><Icon name="hourglass" size={11}/> RUNWAY GENERATING...</span>
              )}
              {runwayVideoUrl&&!runwayGenerating&&(
                <div style={{display:"flex",alignItems:"center",gap:"8px",flexWrap:"wrap"}}>
                  <span style={{fontSize:"9px",color:"#ff9500",fontWeight:"700",letterSpacing:"1px",whiteSpace:"nowrap",background:"rgba(255,149,0,0.1)",border:"1px solid rgba(255,149,0,0.4)",padding:"3px 8px",borderRadius:"4px",animation:"pulse 2s infinite"}}>
                    <Icon name="save" size={12}/> SAVE YOUR VIDEO NOW — link expires when session ends
                  </span>
                  <span style={{fontSize:"9px",color:"#1D9E75",fontWeight:"700",letterSpacing:"1px",whiteSpace:"nowrap",border:"1px solid #1D9E75",padding:"3px 8px",borderRadius:"4px",cursor:"pointer"}}
                    onClick={()=>setShowCFSPlayer(true)}>
                    ▶ PLAY IN CFS
                  </span>
                </div>
              )}
            </div>
          )}
          {/* CREATIVE ASSET WORKSPACE — 24-kraft panel — S-084 */}
          {userMode==="creative"&&(
            <div style={{background:"rgba(8,13,28,0.95)",borderBottom:`1px solid rgba(201,168,76,0.15)`,padding:"8px 16px",display:"flex",alignItems:"center",gap:"12px",flexShrink:0,flexWrap:"wrap"}}>
              <span style={{fontSize:"9px",color:"#C9A84C",fontWeight:"700",letterSpacing:"1px",whiteSpace:"nowrap"}}>RESH CREATIVE STUDIO™</span>
              {/* Asset Upload */}
              <label style={{cursor:"pointer",display:"flex",alignItems:"center",gap:"5px",fontSize:"9px",color:"#F0EDE4",background:"rgba(201,168,76,0.1)",border:"0.5px solid rgba(201,168,76,0.3)",borderRadius:"4px",padding:"4px 8px",whiteSpace:"nowrap"}}>
                <Icon name="paperclip" size={12}/> UPLOAD ASSET
                <input type="file" accept="image/*,video/*,audio/*" style={{display:"none"}}
                  onChange={async e=>{
                    const file=e.target.files[0];
                    if(!file) return;
                    const reader=new FileReader();
                    reader.onload=async ev=>{
                      const b64=ev.target.result.split(",")[1];
                      try{
                        const res=await fetch(`${API_BASE}/api/creative/upload-asset`,{
                          method:"POST",
                          headers:{"Content-Type":"application/json","X-Requested-With":"XMLHttpRequest","Authorization":`Bearer ${authToken}`},
                          body:JSON.stringify({asset_data:b64,asset_type:file.type.split("/")[0],asset_name:file.name,session_id:sessionId})
                        });
                        const d=await res.json();
                        if(d.asset_id){
                          setUploadedAssets(prev=>[...prev,{id:d.asset_id,name:file.name,type:file.type.split("/")[0]}]);
                          alert(`Asset uploaded: ${d.asset_id}`);
                        }
                      }catch(err){alert("Upload failed: "+err.message);}
                    };
                    reader.readAsDataURL(file);
                  }}
                />
              </label>
              {/* Uploaded assets list */}
              {uploadedAssets.map(a=>(
                <span key={a.id} style={{fontSize:"9px",color:"#1D9E75",background:"rgba(29,158,117,0.1)",border:"0.5px solid rgba(29,158,117,0.3)",borderRadius:"4px",padding:"3px 7px",whiteSpace:"nowrap"}}>
                  {a.type==="image"?<Icon name="image" size={12}/>:<Icon name="clapper" size={12}/>} {a.name.length>15?a.name.slice(0,15)+"...":a.name} [{a.id}]
                </span>
              ))}
              {/* Voice generation button */}
              <button
                onClick={async()=>{
                  if(!creativeFreedom && !responses[viewAgent]) return;
                  const textToSpeak=creativeFreedom||"Creative Freedom session is active.";
                  try{
                    const res=await fetch(`${API_BASE}/api/creative/generate-voice`,{
                      method:"POST",
                      headers:{"Content-Type":"application/json","X-Requested-With":"XMLHttpRequest","Authorization":`Bearer ${authToken}`},
                      body:JSON.stringify({text:textToSpeak,session_id:sessionId})
                    });
                    const d=await res.json();
                    if(d.audio_base64){
                      const audio=new Audio(`data:audio/mpeg;base64,${d.audio_base64}`);
                      audio.play();
                    } else { alert(d.error||"Voice generation failed"); }
                  }catch(err){alert("Voice error: "+err.message);}
                }}
                style={{fontSize:"9px",color:"#F0EDE4",background:"rgba(201,168,76,0.1)",border:"0.5px solid rgba(201,168,76,0.3)",borderRadius:"4px",padding:"4px 8px",cursor:"pointer",whiteSpace:"nowrap"}}>
                <Icon name="mic" size={13}/> VOICE
              </button>
            </div>
          )}
          {showOpenBid&&anyComplete&&!approvedAgent&&(
            <div style={{background:T.bg3,borderBottom:`1px solid ${T.border2}`,padding:"10px 16px",flexShrink:0}}>
              <div style={{fontSize:"10px",color:T.gold,fontWeight:"700",letterSpacing:"1px",marginBottom:"6px"}}>OPEN BID — Chair selects the lead</div>
              <div style={{display:"flex",gap:"6px",flexWrap:"wrap"}}>
                {AGENTS.map(agent=>{
                  if(statuses[agent.id]!==STATUS.COMPLETE) return null;
                  return(
                    <button key={agent.id} onClick={()=>approveAgent(agent.id)}
                      style={{padding:"5px 14px",borderRadius:"4px",border:`1px solid ${agent.color}55`,background:`${agent.color}11`,color:agent.color,cursor:"pointer",fontSize:"10px",fontWeight:"700"}}>
                      <Icon name="check" size={11}/> {agent.label}
                    </button>
                  );
                })}
                <button onClick={()=>setShowOpenBid(false)} style={{padding:"5px 10px",borderRadius:"4px",border:`1px solid ${T.border}`,background:"transparent",color:T.textDim,cursor:"pointer",fontSize:"10px",marginLeft:"auto"}}>Dismiss</button>
              </div>
            </div>
          )}

          {anyComplete&&userMode==="creative"&&!showProductionPanel&&(
            <div style={{background:T.bg3,borderBottom:`1px solid ${T.border2}`,padding:"10px 16px",flexShrink:0,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
              <div style={{fontSize:"10px",color:T.gold,fontWeight:"700",letterSpacing:"1px"}}>RESH CREATÏVE STUDIO™ — PRODUCTION PACKAGE</div>
              <button onClick={async()=>{
                try{
                  const ar={};
                  AGENTS.forEach(a=>{if(responses[a.id])ar[a.id]=responses[a.id];});
                  const res=await fetch(`${API_BASE}/api/production/package`,{
                    method:"POST",
                    headers:{"Content-Type":"application/json","Authorization":`Bearer ${authToken}`,"X-Requested-With":"XMLHttpRequest"},
                    body:JSON.stringify({session_id:sessionId,task_id:sessionId+"-pkg",agent_responses:ar,creative_freedom:creativeFreedom,task_type:taskType})
                  });
                  const data=await res.json();
                  if(data.items){
                    setProductionPackage({...data,krafts:Object.fromEntries(data.items.map((it,i)=>[`kraft_0${i+1}_${it.craft.toLowerCase().replace(/ /g,"_")}`,it]))});
                    setShowProductionPanel(true);
                    setPkgEdits({});
                    addAudit("PRODUCTION_PACKAGE_ASSEMBLED","Package "+data.package_id+" — Chair review required");
                  }
                }catch(e){console.error(e);}
              }}
                style={{padding:"6px 18px",borderRadius:"4px",border:`1px solid ${T.gold}`,background:T.gold+"22",color:T.gold,cursor:"pointer",fontSize:"11px",fontWeight:"700"}}>
                <Icon name="bolt" size={13}/> Assemble Production Package
              </button>
            </div>
          )}
          {viewMode==="VIEW"&&(
            <div style={{flex:1,overflowY:"auto",padding:"24px 28px"}}>
              {/* PRODUCTION PACKAGE PANEL — RESH CREATIVE STUDIO™ — S-097 */}
              {userMode==="creative"&&(
                <div style={{marginTop:"16px",padding:"14px",background:T.bg2,border:`1px solid ${T.border2}`,borderRadius:"8px"}}>
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:"10px"}}>
                    <div>
                      <div style={{fontSize:"11px",color:T.gold,fontWeight:"700",letterSpacing:"2px"}}><Icon name="clapper" size={11}/> STAGE 2 — STORYBOARD</div>
                      <div style={{fontSize:"9px",color:T.textMuted,marginTop:"2px"}}>Paste approved script → 6 agents produce storyboard</div>
                    </div>
                    {stage2Output&&<div style={{fontSize:"9px",color:"#4caf50",fontWeight:"700",padding:"2px 8px",background:"rgba(76,175,80,0.1)",borderRadius:"4px"}}><Icon name="check" size={11}/> {stage2Output.agents_completed}/6</div>}
                  </div>
                  <textarea value={lockedScript} onChange={e=>setLockedScript(e.target.value)} placeholder="Paste Chair-approved script from Stage 1..." rows={4} style={{width:"100%",boxSizing:"border-box",background:T.bg3,border:`1px solid ${T.border2}`,borderRadius:"4px",color:T.text,fontSize:"11px",padding:"8px",fontFamily:"monospace",resize:"vertical",marginBottom:"8px"}}/>
                  <button onClick={()=>handleStage2Storyboard(lockedScript)} disabled={stage2Loading||!lockedScript.trim()} style={{width:"100%",padding:"8px",background:"transparent",border:`1px solid ${lockedScript.trim()?T.gold:T.border2}`,color:lockedScript.trim()?T.gold:T.textMuted,borderRadius:"4px",fontSize:"10px",fontWeight:"700",letterSpacing:"2px",cursor:lockedScript.trim()?"pointer":"not-allowed",marginBottom:"10px"}}>
                    {stage2Loading?"⟳ GENERATING...":"GENERATE STORYBOARD — ALL 6 AGENTS"}
                  </button>
                  {stage2Output&&(
                    <div>
                      <div style={{fontSize:"9px",color:T.gold,letterSpacing:"2px",marginBottom:"8px",paddingBottom:"4px",borderBottom:`1px solid ${T.border2}`}}>STORYBOARD OUTPUTS — CHAIR REVIEW REQUIRED</div>
                      {Object.entries(stage2Output.storyboard_outputs||{}).map(([agent,output])=>(
                        <div key={agent} style={{marginBottom:"10px",background:T.bg3,border:`1px solid ${T.border2}`,borderRadius:"6px",overflow:"hidden"}}>
                          <div style={{padding:"6px 10px",background:T.bg2,borderBottom:`1px solid ${T.border2}`,display:"flex",alignItems:"center",gap:"8px"}}>
                            <div style={{width:"5px",height:"5px",borderRadius:"50%",background:T.gold}}/>
                            <span style={{fontSize:"10px",color:T.gold,fontWeight:"700",textTransform:"uppercase"}}>{agent}</span>
                            <span style={{fontSize:"9px",color:T.textMuted}}>{{"claude":"Scene Architect","chatgpt":"Production Designer","gemini":"Continuity Supervisor","grok":"Rights & Clearance","copilot":"Technical Director","perplexity":"Reference Researcher"}[agent]}</span>
                          </div>
                          <div style={{padding:"10px",fontSize:"11px",color:T.textMuted,lineHeight:"1.6",whiteSpace:"pre-wrap",maxHeight:"250px",overflowY:"auto",fontFamily:"monospace"}}>{output}</div>
                        </div>
                      ))}
                      {Object.keys(stage2Output.errors||{}).length>0&&Object.entries(stage2Output.errors).map(([a,e])=>(
                        <div key={a} style={{fontSize:"10px",color:"#ff8888",padding:"4px 0"}}>{a}: {e}</div>
                      ))}
                      <div style={{fontSize:"8px",color:"#333",marginTop:"6px",textAlign:"right"}}>Stage 2 · D.A.V.I.D. Forensic Spine · S-098 · Resh CreAItive Studio™</div>
                    </div>
                  )}
                </div>
              )}
              
              {showProductionPanel&&productionPackage&&(
                <div style={{background:"#080D1C",border:"2px solid rgba(201,168,76,0.4)",borderRadius:"12px",padding:"20px",marginBottom:"20px"}}>
                  {/* Header */}
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:"16px"}}>
                    <div>
                      <div style={{fontSize:"11px",color:"#C9A84C",fontWeight:"700",letterSpacing:"2px"}}><Icon name="clapper" size={11}/> RESH CREATIVE STUDIO™ — PRODUCTION PACKAGE</div>
                      <div style={{fontSize:"9px",color:"#666",marginTop:"2px"}}>{productionPackage.package_id} · Chair review required before generation</div>
                    </div>
                    <button onClick={()=>setShowProductionPanel(false)} style={{background:"transparent",border:"none",color:"#666",cursor:"pointer",fontSize:"16px"}}><Icon name="x" size={15}/></button>
                  </div>

                  {/* Edit Suite — per agent — S-097 */}
                  <div style={{display:"flex",flexDirection:"column",gap:"10px",marginBottom:"16px"}}>
                    {Object.entries(productionPackage.krafts).map(([key,kraft])=>(
                      <div key={key} style={{background:"rgba(255,255,255,0.03)",border:"1px solid rgba(201,168,76,0.15)",borderRadius:"8px",padding:"12px"}}>
                        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:"6px"}}>
                          <span style={{fontSize:"9px",color:"#C9A84C",fontWeight:"700",letterSpacing:"1px"}}>{kraft.label} — EDIT SUITE</span>
                          <div style={{display:"flex",alignItems:"center",gap:"6px"}}>
                            <span style={{fontSize:"9px",color:"#555"}}>Partner:</span>
                            <select
                              value={pkgEdits[key+"_partner"]||"claude"}
                              onChange={e=>setPkgEdits(p=>({...p,[key+"_partner"]:e.target.value}))}
                              style={{background:"#080D1C",color:"#C9A84C",border:"1px solid rgba(201,168,76,0.4)",borderRadius:"3px",padding:"2px 6px",fontSize:"9px",fontWeight:"700",outline:"none"}}>
                              {["claude","chatgpt","gemini","grok","copilot","perplexity"].map(a=>(
                                <option key={a} value={a}>{a.charAt(0).toUpperCase()+a.slice(1)}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <textarea
                          value={pkgEdits[key]!==undefined ? pkgEdits[key] : kraft.content}
                          onChange={e=>setPkgEdits(p=>({...p,[key]:e.target.value}))}
                          style={{width:"100%",minHeight:"80px",background:"rgba(0,0,0,0.3)",border:"1px solid rgba(201,168,76,0.2)",borderRadius:"4px",color:"#F0EDE4",fontSize:"11px",padding:"8px",resize:"vertical",fontFamily:"inherit",boxSizing:"border-box",outline:"none",marginBottom:"6px"}}
                        />
                        <div style={{display:"flex",gap:"6px",alignItems:"center"}}>
                          <input
                            value={pkgEdits[key+"_direction"]||""}
                            onChange={e=>setPkgEdits(p=>({...p,[key+"_direction"]:e.target.value}))}
                            placeholder="Direct the edit — e.g. Make Scene 3 shorter, change tone to urgent..."
                            style={{flex:1,background:"#0e0d14",border:"1px solid rgba(201,168,76,0.2)",borderRadius:"4px",padding:"5px 8px",color:"#F0EDE4",fontSize:"10px",outline:"none"}}
                          />
                          <button
                            onClick={async()=>{
                              const direction = pkgEdits[key+"_direction"]||"";
                              if(!direction.trim()) return;
                              const partner = pkgEdits[key+"_partner"]||"claude";
                              try{
                                const res = await fetch(`${API_BASE}/api/edit/direct`,{
                                  method:"POST",
                                  headers:{"Content-Type":"application/json","Authorization":`Bearer ${authToken}`,"X-Requested-With":"XMLHttpRequest"},
                                  body:JSON.stringify({content:pkgEdits[key]||kraft.content,direction,partner,context:"Chair is directing an edit. Follow the Chair direction exactly. Return only the edited content."})
                                });
                                const data = await res.json();
                                if(data.edited_content) setPkgEdits(p=>({...p,[key]:data.edited_content}));
                                addAudit("EDIT_APPLIED",`${kraft.label} edited by ${partner}`);
                              }catch(e){console.error(e);}
                            }}
                            style={{padding:"5px 12px",borderRadius:"4px",border:"1px solid #C9A84C",background:"rgba(201,168,76,0.1)",color:"#C9A84C",cursor:"pointer",fontSize:"10px",fontWeight:"700",whiteSpace:"nowrap"}}>
                            <Icon name="pencil" size={11}/> Edit
                          </button>
                          <button
                            onClick={()=>{
                              setPkgEdits(p=>({...p,[key+"_locked"]:true}));
                              addAudit("CONTENT_LOCKED",`${kraft.label} — Chair locked`);
                            }}
                            style={{padding:"5px 12px",borderRadius:"4px",border:"1px solid #48bb78",background:"rgba(72,187,120,0.1)",color:"#48bb78",cursor:"pointer",fontSize:"10px",fontWeight:"700",whiteSpace:"nowrap"}}>
                            {pkgEdits[key+"_locked"]?<><Icon name="lock" size={11}/> Locked</>:<><Icon name="unlock" size={11}/> Lock</>}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* AI / Video / Audio / Editing dropdowns + Chair Approves */}
                  <div style={{background:"rgba(201,168,76,0.05)",border:"1px solid rgba(201,168,76,0.2)",borderRadius:"8px",padding:"14px"}}>
                    <div style={{fontSize:"9px",color:"#C9A84C",fontWeight:"700",letterSpacing:"1px",marginBottom:"10px"}}>CHAIR SELECTS ENGINES — Approve and Generate</div>
                    <div style={{display:"flex",gap:"12px",flexWrap:"wrap",marginBottom:"12px",alignItems:"flex-end"}}>
                      {[
                        {label:"AI",key:"ai_engine",opts:["Claude","ChatGPT","Gemini","Grok","Copilot","Perplexity"]},
                        {label:"VIDEO",key:"video_engine",opts:["Runway","Pika","Kling","Stability AI"]},
                        {label:"AUDIO",key:"audio_engine",opts:["ElevenLabs","Suno","Udio"]},
                        {label:"EDITING",key:"edit_engine",opts:["DaVinci Resolve","Descript","Opus Clip","Adobe Audition","Auphonic","CapCut"]},
                      ].map(({label,key,opts})=>(
                        <div key={key} style={{display:"flex",flexDirection:"column",gap:"3px"}}>
                          <div style={{fontSize:"9px",color:"#888",fontWeight:"700",letterSpacing:"1px"}}>{label}</div>
                          <select
                            value={pkgEdits[key]||opts[0]}
                            onChange={e=>setPkgEdits(p=>({...p,[key]:e.target.value}))}
                            style={{background:"#080D1C",color:"#C9A84C",border:"1px solid rgba(201,168,76,0.5)",borderRadius:"4px",padding:"5px 10px",fontSize:"11px",fontWeight:"700",outline:"none"}}>
                            {opts.map(o=><option key={o} value={o}>{o}</option>)}
                          </select>
                        </div>
                      ))}
                    </div>
                    <div style={{display:"flex",gap:"10px",alignItems:"flex-start"}}>
                      <textarea
                        value={pkgEdits["approved_prompt"]!==undefined ? pkgEdits["approved_prompt"] : (productionPackage.krafts?.kraft_01_script?.content||"")}
                        onChange={e=>setPkgEdits(p=>({...p,approved_prompt:e.target.value}))}
                        placeholder="Edit the approved prompt that will be sent to the engine..."
                        style={{flex:1,minHeight:"60px",background:"#080D1C",border:"1px solid rgba(201,168,76,0.4)",borderRadius:"6px",color:"#F0EDE4",fontSize:"11px",padding:"8px 12px",resize:"vertical",fontFamily:"inherit",outline:"none",boxSizing:"border-box"}}
                      />
                      <button
                        disabled={pkgGenerating}
                        onClick={async()=>{
                          const approvedPrompt = pkgEdits["approved_prompt"]!==undefined
                            ? pkgEdits["approved_prompt"]
                            : (productionPackage.krafts?.kraft_01_script?.content||"");
                          if(!approvedPrompt.trim()){alert("Approved prompt is required");return;}
                          if(!window.confirm(`Chair confirms: Send approved prompt to ${selectedEngine.toUpperCase()}?

Prompt: "${approvedPrompt.slice(0,100)}..."

This action will be logged to D.A.V.I.D. Forensic Spine.`)) return;
                          setPkgGenerating(true);
                          addAudit("CHAIR_APPROVED_GENERATION",`Engine: ${selectedEngine} — Package: ${productionPackage.package_id}`,true);
                          try{
                            const genRes = await fetch(`${API_BASE}/api/creative/production-generate`,{
                              method:"POST",
                              headers:{"Content-Type":"application/json","X-Requested-With":"XMLHttpRequest","Authorization":`Bearer ${authToken}`},
                              body:JSON.stringify({
                                chair_approved:true,
                                approved_prompt:approvedPrompt,
                                engine_id:selectedEngine,
                                session_id:sessionId,
                                package_id:productionPackage.package_id,
                                creative_freedom:creativeFreedom,
                              })
                            });
                            const genData = await genRes.json();
                            if(genData.task_id){
                              setRunwayGenerating(true);
                              setShowProductionPanel(false);
                              addAudit("GENERATION_STARTED",`Runway task ${genData.task_id} — polling for result`,true);
                              // Poll for video
                              const poll = setInterval(async()=>{
                                try{
                                  const stRes = await fetch(`${API_BASE}/api/creative/video-status?task_id=${genData.task_id}`,{
                                    headers:{"Authorization":`Bearer ${authToken}`}
                                  });
                                  if(stRes.ok){
                                    const stData = await stRes.json();
                                    if(stData.status==="SUCCEEDED"||stData.output_url){
                                      clearInterval(poll);
                                      setRunwayVideoUrl(stData.output_url||"");
                                      setRunwayGenerating(false);
                                      setShowCFSPlayer(true);
                                    }
                                  }
                                }catch(e){clearInterval(poll);setRunwayGenerating(false);}
                              },3000);
                              setTimeout(()=>{clearInterval(poll);setRunwayGenerating(false);},120000);
                            } else if(genData.status==="ENGINE_PENDING"){
                              alert(genData.message);
                            } else {
                              alert(genData.error||"Generation failed");
                            }
                          }catch(e){alert("Generation error: "+e.message);}
                          finally{setPkgGenerating(false);}
                        }}
                        style={{padding:"0 20px",height:"60px",background:pkgGenerating?"#333":"#C9A84C",color:"#080D1C",border:"none",borderRadius:"6px",cursor:pkgGenerating?"not-allowed":"pointer",fontSize:"11px",fontWeight:"800",minWidth:"100px",flexShrink:0}}>
                        {pkgGenerating?<><Icon name="hourglass" size={12}/> Sending...</>:<><Icon name="clapper" size={12}/> GENERATE — Chair Approved</>}
                      </button>
                    </div>
                    <div style={{fontSize:"9px",color:"#555",marginTop:"8px"}}>Chair approval logged to D.A.V.I.D. Forensic Spine · John 14:26 CJB · RESH CREATIVE STUDIO™™</div>
                  </div>
                </div>
              )}

              {/* CFS — Creative Film Studio — video player + Kraft tools */}
              {showCFSPlayer&&runwayVideoUrl&&(
                <div style={{background:"#080D1C",border:"1px solid rgba(201,168,76,0.3)",borderRadius:"10px",padding:"16px",marginBottom:"20px"}}>
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:"10px"}}>
                    <span style={{fontSize:"10px",color:"#C9A84C",fontWeight:"700",letterSpacing:"2px"}}><Icon name="clapper" size={12}/> RESH CREATIVE STUDIO™</span>
                    <button onClick={()=>setShowCFSPlayer(false)} style={{background:"transparent",border:"none",color:"#666",cursor:"pointer",fontSize:"14px"}}><Icon name="x" size={14}/></button>
                  </div>
                  {/* Video player */}
                  <video
                    key={runwayVideoUrl}
                    controls
                    autoPlay
                    loop
                    style={{width:"100%",borderRadius:"6px",maxHeight:"400px",background:"#000"}}
                    crossOrigin="anonymous"
                    onError={e=>{
                      e.target.style.display="none";
                      document.getElementById("cfs-fallback").style.display="block";
                    }}
                  >
                    <source src={runwayVideoUrl} type="video/mp4"/>
                  </video>
                  <div id="cfs-fallback" style={{display:"none",padding:"20px",textAlign:"center"}}>
                    <a href={runwayVideoUrl} target="_blank" rel="noopener noreferrer"
                      style={{color:"#1D9E75",fontSize:"12px",fontWeight:"700"}}>
                      ▶ Open video in new tab (CDN auth required)
                    </a>
                  </div>
                  {/* Kraft tools */}
                  <div style={{marginTop:"12px",display:"flex",gap:"8px",flexWrap:"wrap"}}>
                    <button
                      onClick={()=>{
                        const a=document.createElement("a");
                        a.href=runwayVideoUrl;
                        a.download="kraft_video.mp4";
                        a.click();
                      }}
                      style={{fontSize:"9px",color:"#F0EDE4",background:"rgba(201,168,76,0.1)",border:"0.5px solid rgba(201,168,76,0.3)",borderRadius:"4px",padding:"5px 10px",cursor:"pointer"}}>
                      <Icon name="download" size={13}/> DOWNLOAD
                    </button>
                    <button
                      onClick={()=>{
                        const vid=document.querySelector("#cfs-player video");
                        if(!vid) return;
                        const canvas=document.createElement("canvas");
                        canvas.width=vid.videoWidth;
                        canvas.height=vid.videoHeight;
                        canvas.getContext("2d").drawImage(vid,0,0);
                        const frame=canvas.toDataURL("image/png").split(",")[1];
                        setCapturedFrame(frame);
                        setUploadedAssets(prev=>[...prev,{id:"FRAME-"+Date.now(),name:"captured_frame.png",type:"image",b64:frame}]);
                        alert("Frame captured — added to assets for character consistency");
                      }}
                      style={{fontSize:"9px",color:"#F0EDE4",background:"rgba(201,168,76,0.1)",border:"0.5px solid rgba(201,168,76,0.3)",borderRadius:"4px",padding:"5px 10px",cursor:"pointer"}}>
                      <Icon name="camera" size={13}/> CAPTURE FRAME
                    </button>
                    <button
                      onClick={async()=>{
                        const extPrompt=`Continue this scene: ${creativeFreedom}`;
                        try{
                          const res=await fetch(`${API_BASE}/api/creative/generate-video`,{
                            method:"POST",
                            headers:{"Content-Type":"application/json","X-Requested-With":"XMLHttpRequest","Authorization":`Bearer ${authToken}`},
                            body:JSON.stringify({prompt:extPrompt,duration:10,model:"gen3a_turbo",session_id:sessionId})
                          });
                          const d=await res.json();
                          if(d.task_id){
                            setRunwayGenerating(true);
                            setShowCFSPlayer(false);
                            alert("Extending scene — Runway generating next sequence...");
                          }
                        }catch(err){alert("Extend failed: "+err.message);}
                      }}
                      style={{fontSize:"9px",color:"#1D9E75",background:"rgba(29,158,117,0.1)",border:"0.5px solid rgba(29,158,117,0.3)",borderRadius:"4px",padding:"5px 10px",cursor:"pointer"}}>
                      <Icon name="plus" size={13}/> EXTEND SCENE
                    </button>
                    <span style={{fontSize:"9px",color:"#555",alignSelf:"center",marginLeft:"auto"}}>
                      RESH CREATIVE STUDIO™ — Video Scene · Runway gen4.5
                    </span>
                  </div>
                  {/* Kraft navigation */}
                  <div style={{marginTop:"12px",borderTop:"1px solid rgba(201,168,76,0.15)",paddingTop:"10px"}}>
                    <div style={{fontSize:"9px",color:"#C9A84C",fontWeight:"700",letterSpacing:"1px",marginBottom:"8px"}}>Resh Creatives — Next production element</div>
                    <div style={{display:"flex",gap:"6px",flexWrap:"wrap"}}>
                      {[
                        {k:11,label:"VOICE",type:"AUDIO"},
                        {k:13,label:"MUSIC",type:"MUSIC"},
                        {k:15,label:"SFX",type:"AUDIO"},
                        {k:6,label:"CONTINUITY",type:"STORYBOARD"},
                        {k:7,label:"RIGHTS",type:"SCRIPT"},
                      ].map(kft=>(
                        <button key={kft.k}
                          onClick={()=>{
                            setTaskType(kft.type);
                            setShowCFSPlayer(false);
                          }}
                          style={{fontSize:"9px",color:"#F0EDE4",background:"rgba(255,255,255,0.05)",border:"0.5px solid rgba(255,255,255,0.1)",borderRadius:"4px",padding:"4px 8px",cursor:"pointer"}}>
                          {kft.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              {isSharing&&screenFrame&&(
                <div style={{marginBottom:"16px",background:T.bg2,border:"1px solid #48bb7844",borderRadius:"8px",padding:"10px"}}>
                  <div style={{fontSize:"9px",color:"#48bb78",fontWeight:"700",letterSpacing:"1px",marginBottom:"8px",display:"flex",alignItems:"center",gap:"8px"}}>
                    <span><Icon name="screen" size={12}/> SCREEN PREVIEW — What Claude · ChatGPT · Gemini will see</span>
                    <span style={{marginLeft:"auto",fontSize:"9px",color:T.textDim}}>Updates every 3s</span>
                  </div>
                  <img src={`data:image/jpeg;base64,${screenFrame}`} alt="Screen preview"
                    style={{width:"100%",borderRadius:"4px",border:"1px solid #48bb7822",display:"block"}}/>
                </div>
              )}
              {!currentTask&&(
                <div style={{textAlign:"center",marginTop:"80px"}}>
                  <div style={{fontSize:"32px",fontWeight:"700",color:T.gold,marginBottom:"12px"}}>UandAI™</div>
                  <div style={{fontSize:"14px",color:T.textDim,lineHeight:"1.9",marginBottom:"20px"}}>
                    The AI Listens. The Human Decides.<br/>
                    Select a project · Choose task type · Submit to all 6 agents<br/>
                    Chair reviews · Approves the lead · View shows the work
                  </div>
                  <div style={{fontSize:"11px",color:T.gold+"22",letterSpacing:"3px",fontWeight:"700"}}>SEE · AUDIT · VERIFY · STOP</div>
                </div>
              )}
              {viewData&&responses[viewAgent]&&(
                <div>
                  <div style={{display:"flex",alignItems:"center",gap:"10px",marginBottom:"12px"}}>
                    <span style={{fontSize:"12px",color:viewData.color,fontWeight:"700",letterSpacing:"1px",textTransform:"uppercase"}}>{viewData.label}</span>
                    {isCustom&&<span style={{fontSize:"10px",color:T.textDim}}>· {agentRoles[viewAgent]}</span>}
                    {approvedAgent===viewAgent&&<span style={{fontSize:"9px",color:"#48bb78",fontWeight:"700",background:"#0f2010",border:"1px solid #48bb7844",padding:"1px 7px",borderRadius:"3px"}}>CHAIR APPROVED · LEAD</span>}
                    <div style={{marginLeft:"auto",display:"flex",gap:"6px"}}>
                      {AGENTS.filter(a=>a.id!==viewAgent&&responses[a.id]).map(a=>(
                        <button key={a.id} onClick={()=>grantView(a.id)}
                          style={{padding:"3px 8px",borderRadius:"3px",border:`1px solid ${a.color}44`,background:`${a.color}11`,color:a.color,cursor:"pointer",fontSize:"9px",fontWeight:"700"}}>
                          {a.label}
                        </button>
                      ))}
                      <button onClick={()=>grantView(null)} style={{padding:"3px 8px",borderRadius:"3px",border:"1px solid #e53e3e44",background:"#1a0808",color:"#e53e3e",cursor:"pointer",fontSize:"9px"}}>Clear</button>
                    </div>
                  </div>
                  <div style={{fontSize:"14px",color:T.responseTx,lineHeight:"1.85",whiteSpace:"pre-wrap",wordBreak:"break-word",background:T.cardBg,border:`1px solid ${viewData.color}22`,borderRadius:"10px",padding:"24px 28px"}}>
                    {DOMPurify.sanitize(responses[viewAgent])}
                  </div>
                </div>
              )}
              {currentTask&&!viewData&&(
                <div style={{textAlign:"center",color:T.textDim,fontSize:"13px",marginTop:"40px"}}>
                  Agents are responding… Select an agent above or approve a lead from the Open Bid.
                </div>
              )}
            </div>
          )}

          {viewMode==="BOARD"&&(
            <div style={{flex:1,overflowY:"auto",padding:"16px"}}>
              {!currentTask&&(
                <div style={{textAlign:"center",marginTop:"80px"}}>
                  <div style={{fontSize:"28px",fontWeight:"700",color:T.gold,marginBottom:"10px"}}>Board Mode</div>
                  <div style={{fontSize:"13px",color:T.textDim,lineHeight:"1.8"}}>All 6 agent responses visible simultaneously.<br/>Submit a task to see all agents respond in parallel.</div>
                </div>
              )}
              {currentTask&&(
                <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1fr 1fr",gap:"12px"}}>
                  {AGENTS.map(agent=>{
                    const s=statuses[agent.id];
                    const r=DOMPurify.sanitize(responses[agent.id]||"");
                    const ss=STATUS_STYLE[s]||STATUS_STYLE.IDLE;
                    const isApproved=approvedAgent===agent.id;
                    const isExpanded=expandedBoard===agent.id;
                    const isRerunning=rerunAgent===agent.id;
                    const hasPrev=!!prevResponses[agent.id];
                    const comparing=showCompare[agent.id]&&hasPrev;
                    return(
                      <div key={agent.id} style={{background:T.cardBg,border:`1px solid ${isApproved?"#48bb78":agent.color+"22"}`,borderRadius:"8px",padding:"12px",display:"flex",flexDirection:"column",minHeight:"160px"}}>
                        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:"8px"}}>
                          <div>
                            <span style={{fontSize:"11px",fontWeight:"700",color:agent.color}}>{agent.label}{isApproved?<><Icon name="check" size={9}/> LEAD</>:null}</span>
                            {/* Role dropdown on board card */}
                            <div style={{marginTop:"3px"}}>
                              <select
                                value={agentRoles[agent.id]}
                                onChange={e=>{
                                  const newRole=e.target.value;
                                  setAgentRoles(p=>({...p,[agent.id]:newRole}));
                                  addAudit("ROLE_ASSIGNED",`${agent.label} → ${newRole}`,true);
                                }}
                                style={{fontSize:"8px",background:T.inputBg,border:`1px solid ${T.gold}44`,color:T.gold,borderRadius:"3px",padding:"1px 4px",cursor:"pointer"}}>
                                <option value={agent.label}>{agent.label} (default)</option>
                                {(userMode==="creative"?["Screenwriter","Production Designer","Continuity Supervisor","Rights & Clearance","Technical Director","Reference Researcher"]:ALL_ROLES).map(role=><option key={role} value={role}>{role}</option>)}
                              </select>
                            </div>
                          </div>
                          <div style={{display:"flex",gap:"4px",alignItems:"center",flexDirection:"column"}}>
                            <div style={{display:"flex",gap:"4px"}}>
                              <div style={{fontSize:"8px",fontWeight:"700",padding:"1px 5px",borderRadius:"3px",color:ss.color,background:ss.bg,border:`1px solid ${ss.border}`}}>
                                {s===STATUS.RESPONDING?"● ":""}{s}
                              </div>
                              {r&&<button onClick={()=>setExpandedBoard(isExpanded?null:agent.id)}
                                style={{fontSize:"8px",padding:"1px 5px",borderRadius:"3px",border:`1px solid ${agent.color}44`,background:`${agent.color}11`,color:agent.color,cursor:"pointer"}}>
                                {isExpanded?<Icon name="arrowDownLeft" size={11}/>:<Icon name="arrowUpRight" size={11}/>}
                              </button>}
                            </div>
                            {r&&!isRerunning&&(
                              <button onClick={()=>rerunSingleAgent(agent.id)}
                                style={{fontSize:"8px",padding:"1px 6px",borderRadius:"3px",border:`1px solid ${agent.color}55`,background:`${agent.color}11`,color:agent.color,cursor:"pointer",fontWeight:"700"}}>
                                <Icon name="rerun" size={12}/> Rerun
                              </button>
                            )}
                            {isRerunning&&<span style={{fontSize:"8px",color:T.gold}}>Running…</span>}
                          </div>
                        </div>
                        <div style={{fontSize:"11px",color:r?T.agentTx:T.textDim,lineHeight:"1.55",whiteSpace:"pre-wrap",wordBreak:"break-word",fontStyle:r?"normal":"italic",flex:1,maxHeight:isExpanded?"none":"600px",overflow:"auto"}}>
                          {r||(s===STATUS.RESPONDING?"Processing…":"Awaiting task…")}
                          {agent.id==="chatgpt"&&taskType==="SECURITY"&&(
                            <div style={{fontSize:"9px",color:T.gold,marginTop:"4px",padding:"3px 6px",background:"#1a1200",border:`1px solid ${T.gold}33`,borderRadius:"3px",lineHeight:"1.4"}}>
                              <Icon name="warning" size={12}/> CodeGenerator does not perform patent searches. Grok — SecurityStressTester is the designated IP clearance agent for SECURITY tasks.
                            </div>
                          )}
                        </div>
                        {/* Comparison panel on board */}
                        {comparing&&(
                          <div style={{marginTop:"8px",borderTop:`1px solid ${T.border}`,paddingTop:"8px"}}>
                            <div style={{display:"flex",justifyContent:"space-between",marginBottom:"4px"}}>
                              <span style={{fontSize:"8px",color:T.gold,fontWeight:"700",letterSpacing:"1px"}}>BEFORE / AFTER</span>
                              <button onClick={()=>setShowCompare(p=>({...p,[agent.id]:false}))}
                                style={{fontSize:"8px",background:"transparent",border:"none",color:T.textDim,cursor:"pointer"}}><Icon name="x" size={11}/></button>
                            </div>
                            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"6px"}}>
                              <div>
                                <div style={{fontSize:"8px",color:T.textDim,marginBottom:"2px"}}>BEFORE</div>
                                <div style={{fontSize:"9px",color:T.textMuted,background:T.bg2,border:`1px solid ${T.border}`,borderRadius:"3px",padding:"4px 6px",maxHeight:"100px",overflowY:"auto",whiteSpace:"pre-wrap",wordBreak:"break-word"}}>
                                  {prevResponses[agent.id]||"—"}
                                </div>
                              </div>
                              <div>
                                <div style={{fontSize:"8px",color:agent.color,marginBottom:"2px"}}>AFTER — {agentRoles[agent.id]}</div>
                                <div style={{fontSize:"9px",color:T.agentTx,background:T.bg2,border:`1px solid ${agent.color}44`,borderRadius:"3px",padding:"4px 6px",maxHeight:"100px",overflowY:"auto",whiteSpace:"pre-wrap",wordBreak:"break-word"}}>
                                  {responses[agent.id]||"—"}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                        {r&&!isApproved&&(
                          <button onClick={()=>approveAgent(agent.id)}
                            style={{marginTop:"8px",width:"100%",padding:"4px 0",borderRadius:"3px",border:`1px solid ${agent.color}44`,background:`${agent.color}11`,color:agent.color,cursor:"pointer",fontSize:"9px",fontWeight:"700"}}>
                            <Icon name="check" size={11}/> Select as Lead
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* TASK INPUT */}
          <div style={{background:T.bg2,borderTop:`1px solid ${T.border}`,padding:"10px 12px",flexShrink:0,position:isMobile?"sticky":"relative",bottom:0,zIndex:10}}>
            {isSharing&&<div style={{fontSize:"9px",color:"#48bb78",marginBottom:"6px"}}><Icon name="screen" size={11}/> Screen frame attached on submit — Claude · ChatGPT · Gemini will see your screen</div>}
          {isCustom&&(
            <div style={{marginBottom:"8px",display:"flex",gap:"8px",alignItems:"center"}}>
              <select value={taskMode} onChange={e=>setTaskMode(e.target.value)}
                style={{flex:1,background:"#0a1628",border:"1px solid #C9A84C44",color:"#C9A84C",padding:"6px 10px",borderRadius:"6px",fontSize:"11px",fontWeight:"700",cursor:"pointer"}}>
                {TASK_MODES.map(m=><option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
              {taskMode==="custom"&&(
                <input value={customModeText} onChange={e=>setCustomModeText(e.target.value)}
                  placeholder="Type role instruction for all agents..."
                  style={{flex:1,background:"#0a1628",border:"1px solid #C9A84C44",color:"#f0ede4",padding:"6px 10px",borderRadius:"6px",fontSize:"11px"}}/>
              )}
            </div>
          )}
            <div style={{display:"flex",gap:"10px",alignItems:"flex-end"}}>
              <textarea value={task} onChange={e=>setTask(e.target.value)}
                onKeyDown={e=>{if(e.key==="Enter"&&!e.shiftKey)submitTask();}}
                placeholder={`What do you want to accomplish? [${taskType}] — Enter to submit`}
                disabled={halted}
                style={{flex:1,background:T.inputBg,border:`1px solid ${isSharing?"#48bb7844":T.border2}`,color:T.text,padding:"10px 14px",borderRadius:"6px",fontSize:"13px",fontFamily:"inherit",resize:"none",height:"52px",boxSizing:"border-box",outline:"none"}}
              />
              <button onClick={submitTask} disabled={halted||!task.trim()}
                style={{background:task.trim()&&!halted?T.gold:T.gold+"33",border:"none",color:"#080D1C",padding:"0 20px",height:"52px",borderRadius:"6px",cursor:task.trim()&&!halted?"pointer":"default",fontSize:"12px",fontWeight:"700",minWidth:"100px"}}>
                Submit <Icon name="enter" size={12}/><br/><span style={{fontSize:"9px",fontWeight:"400"}}>{isSharing?"Screen + 6":"All 6 agents"}</span>
              </button>
            </div>
          </div>
        </div>

        {/* RIGHT PANEL */}
        <div style={{width:isMobile?"100%":"240px",minWidth:isMobile?"unset":"240px",borderLeft:isMobile?"none":`1px solid ${T.border}`,borderTop:isMobile?`1px solid ${T.border}`:"none",display:"flex",flexDirection:"column",overflowY:"auto",flexShrink:0,maxHeight:isMobile?"320px":"none"}}>
          <div style={{padding:"8px 12px",borderBottom:`1px solid ${T.border}`,fontSize:"10px",color:T.textMuted,fontWeight:"700",letterSpacing:"1.5px",flexShrink:0,background:T.bg2}}>
            6 AGENTS — PARALLEL
          </div>
          {AGENTS.map(agent=>{
            const s=statuses[agent.id];
            const r=DOMPurify.sanitize(responses[agent.id]||"");
            const isView=viewAgent===agent.id;
            const isApproved=approvedAgent===agent.id;
            const isConnected=!!connectedAgents[agent.id]||!!pailKeys[agent.id];
            const ss=STATUS_STYLE[s]||STATUS_STYLE.IDLE;
            const isRerunning=rerunAgent===agent.id;
            const hasPrev=!!prevResponses[agent.id];
            const comparing=showCompare[agent.id]&&hasPrev;
            return(
              <div key={agent.id} style={{borderBottom:`1px solid ${T.border}`,padding:"8px 10px",background:isView?T.bg3:T.bg,flexShrink:0}}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:"4px"}}>
                  <div>
                    <div style={{fontSize:"11px",fontWeight:"700",color:isView?agent.color:T.text,display:"flex",alignItems:"center",gap:"4px"}}>
                      {agent.label}
                      {isApproved&&<span style={{fontSize:"8px",color:"#48bb78",fontWeight:"700"}}><Icon name="check" size={10}/></span>}
                      {isConnected&&<span style={{fontSize:"8px",color:"#48bb78"}}>●</span>}
                      {agent.vision&&isSharing&&<span style={{fontSize:"8px",color:"#63b3ed"}}><Icon name="eye" size={10}/></span>}
                    </div>
                    {/* Role dropdown — always visible, no restriction */}
                    <select
                      value={agentRoles[agent.id]}
                      onChange={e=>{
                        const newRole=e.target.value;
                        setAgentRoles(p=>({...p,[agent.id]:newRole}));
                        addAudit("ROLE_ASSIGNED",`${agent.label} → ${newRole}`,true);
                      }}
                      style={{marginTop:"2px",fontSize:"8px",background:T.inputBg,border:`1px solid ${T.gold}44`,color:T.gold,borderRadius:"3px",padding:"1px 3px",cursor:"pointer",maxWidth:"130px"}}>
                      <option value={agent.label}>{agent.label} (default)</option>
                      {(userMode==="creative"?["Screenwriter","Production Designer","Continuity Supervisor","Rights & Clearance","Technical Director","Reference Researcher"]:ALL_ROLES).map(r=><option key={r} value={r}>{r}</option>)}
                    </select>
                  </div>
                  <div style={{display:"flex",gap:"4px",alignItems:"center",flexDirection:"column"}}>
                    <div style={{display:"flex",gap:"4px"}}>
                      <div style={{fontSize:"8px",fontWeight:"700",padding:"1px 5px",borderRadius:"3px",color:ss.color,background:ss.bg,border:`1px solid ${ss.border}`}}>
                        {s===STATUS.RESPONDING?"● ":""}{s}
                      </div>
                      {viewMode==="VIEW"&&(
                        <button onClick={()=>isView?grantView(null):grantView(agent.id)}
                          style={{fontSize:"8px",fontWeight:"700",padding:"2px 6px",borderRadius:"3px",cursor:"pointer",border:isView?`1px solid ${agent.color}`:`1px solid ${T.gold}44`,background:isView?agent.color:"transparent",color:isView?"#080D1C":T.gold}}>
                          {isView?"▶":"VIEW"}
                        </button>
                      )}
                    </div>
                    {/* Rerun button — only when response exists */}
                    {r&&!isRerunning&&(
                      <button onClick={()=>rerunSingleAgent(agent.id)}
                        title={`Re-run ${agent.label} as ${agentRoles[agent.id]}`}
                        style={{fontSize:"8px",padding:"1px 6px",borderRadius:"3px",border:`1px solid ${agent.color}55`,background:`${agent.color}11`,color:agent.color,cursor:"pointer",fontWeight:"700",whiteSpace:"nowrap"}}>
                        <Icon name="rerun" size={12}/> Rerun
                      </button>
                    )}
                    {isRerunning&&(
                      <span style={{fontSize:"8px",color:T.gold}}>Running…</span>
                    )}
                  </div>
                </div>
                <div style={{height:"68px",overflowY:"auto",fontSize:"10px",color:r?T.agentTx:T.textDim,lineHeight:"1.5",whiteSpace:"pre-wrap",wordBreak:"break-word",fontStyle:r?"normal":"italic",background:T.cardBg,border:`1px solid ${T.border}`,borderRadius:"3px",padding:"4px 6px"}}>
                  {r||(s===STATUS.RESPONDING?"Processing…":"Awaiting task…")}
                  {agent.id==="chatgpt"&&taskType==="SECURITY"&&(
                    <div style={{fontSize:"9px",color:T.gold,marginTop:"4px",padding:"3px 6px",background:"#1a1200",border:`1px solid ${T.gold}33`,borderRadius:"3px",lineHeight:"1.4"}}>
                      <Icon name="warning" size={12}/> CodeGenerator does not perform patent searches. Grok — SecurityStressTester is the designated IP clearance agent for SECURITY tasks.
                    </div>
                  )}
                </div>
                {/* Comparison panel — before/after role swap */}
                {comparing&&(
                  <div style={{marginTop:"6px",borderTop:`1px solid ${T.border}`,paddingTop:"6px"}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"4px"}}>
                      <span style={{fontSize:"8px",color:T.gold,fontWeight:"700",letterSpacing:"1px"}}>BEFORE / AFTER ROLE SWAP</span>
                      <button onClick={()=>setShowCompare(p=>({...p,[agent.id]:false}))}
                        style={{fontSize:"8px",background:"transparent",border:"none",color:T.textDim,cursor:"pointer"}}><Icon name="x" size={11}/></button>
                    </div>
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"4px"}}>
                      <div>
                        <div style={{fontSize:"8px",color:T.textDim,marginBottom:"2px"}}>BEFORE</div>
                        <div style={{fontSize:"9px",color:T.textMuted,background:T.bg2,border:`1px solid ${T.border}`,borderRadius:"3px",padding:"4px 6px",maxHeight:"80px",overflowY:"auto",whiteSpace:"pre-wrap",wordBreak:"break-word"}}>
                          {prevResponses[agent.id]||"—"}
                        </div>
                      </div>
                      <div>
                        <div style={{fontSize:"8px",color:agent.color,marginBottom:"2px"}}>AFTER — {agentRoles[agent.id]}</div>
                        <div style={{fontSize:"9px",color:T.agentTx,background:T.bg2,border:`1px solid ${agent.color}44`,borderRadius:"3px",padding:"4px 6px",maxHeight:"80px",overflowY:"auto",whiteSpace:"pre-wrap",wordBreak:"break-word"}}>
                          {responses[agent.id]||"—"}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* FORENSIC SPINE */}
      <div style={{background:T.bg2,borderTop:`1px solid ${T.border2}`,flexShrink:0}}>
        <div onClick={()=>setSpineOpen(o=>!o)} style={{padding:"5px 20px",display:"flex",alignItems:"center",justifyContent:"space-between",cursor:"pointer",userSelect:"none"}}>
          <div style={{display:"flex",alignItems:"center",gap:"12px"}}>
            <span style={{fontSize:"10px",fontWeight:"700",color:T.gold,letterSpacing:"1.5px"}}>D.A.V.I.D. FORENSIC SPINE™</span>
            <span style={{fontSize:"9px",color:T.textDim}}>{auditLog.length} entries · {viewHistory.length} view events</span>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:"10px"}}>
            {!isMobile&&<span style={{fontSize:"9px",color:T.textDim}}>HMAC-SHA256 · V.I.J.A.Y. Protocol · {sessionId}</span>}
            <span style={{color:T.gold,fontSize:"11px"}}>{spineOpen?"▼":"▲"}</span>
          </div>
        </div>
        {spineOpen&&(
          <div style={{borderTop:`1px solid ${T.border}`,height:"200px",display:"flex",flexDirection:"column"}}>
            <div style={{display:"flex",gap:"4px",padding:"6px 16px",borderBottom:`1px solid ${T.border}`,flexShrink:0,alignItems:"center"}}>
              {[["AUDIT","Audit Log"],["VERIFY","HMAC Verify"],["HISTORY","View History"]].map(([tab,label])=>(
                <button key={tab} onClick={e=>{e.stopPropagation();setSpineTab(tab);}}
                  style={{fontSize:"10px",fontWeight:"700",padding:"3px 10px",borderRadius:"4px",cursor:"pointer",border:spineTab===tab?`1px solid ${T.gold}55`:`1px solid ${T.border}`,background:spineTab===tab?T.gold+"22":"transparent",color:spineTab===tab?T.gold:T.textMuted}}>
                  {label}
                </button>
              ))}
              <span style={{marginLeft:"auto",fontSize:"9px",color:T.textDim}}>Experience the Reality · John 14:26</span>
            </div>
            <div style={{flex:1,overflowY:"auto",padding:"8px 16px",display:"flex",gap:"8px",flexWrap:"wrap",alignContent:"flex-start"}}>
              {spineTab==="AUDIT"&&(
                <>
                  {auditLog.length===0&&<div style={{fontSize:"10px",color:T.textDim,fontStyle:"italic",width:"100%"}}>Every action logged before it executes.</div>}
                  {auditLog.map(e=>(
                    <div key={e.id} style={{fontSize:"10px",borderLeft:`2px solid ${e.hi?T.gold:T.gold+"33"}`,padding:"3px 8px",background:e.hi?T.gold+"08":"transparent",borderRadius:"0 3px 3px 0",minWidth:"160px",maxWidth:"280px"}}>
                      <span style={{color:T.gold+"99",marginRight:"6px",fontSize:"9px"}}>{e.time}</span>
                      <span style={{color:e.hi?T.gold:T.textMuted,fontWeight:"700",fontSize:"9px"}}>{e.event}</span>
                      {e.detail&&<div style={{color:T.agentTx,marginTop:"1px",fontSize:"9px"}}>{e.detail}</div>}
                    </div>
                  ))}
                </>
              )}
              {spineTab==="VERIFY"&&(
                <div style={{width:"100%"}}>
                  <div style={{fontSize:"10px",color:T.gold,fontWeight:"700",marginBottom:"8px",letterSpacing:"1px"}}>
                    HMAC-SHA256 CRYPTOGRAPHIC PROOF — D.A.V.I.D. Forensic Spine
                  </div>
                  <div style={{fontSize:"9px",color:T.textDim,marginBottom:"10px"}}>
                    Every entry below is signed with HMAC-SHA256 anchored to this session. Append-only at database level. Tamper-evident. Court-admissible.
                  </div>
                  {auditLog.length===0&&(
                    <div style={{fontSize:"10px",color:T.textDim,fontStyle:"italic"}}>No entries yet. Submit a task to generate Spine entries.</div>
                  )}
                  {auditLog.slice().reverse().map(e=>(
                    <div key={e.id} style={{fontSize:"10px",borderLeft:"2px solid #48bb78",padding:"4px 10px",marginBottom:"4px",background:"#0f201011",borderRadius:"0 4px 4px 0",minWidth:"280px"}}>
                      <div style={{display:"flex",gap:"10px",alignItems:"center"}}>
                        <span style={{color:"#48bb78",fontWeight:"700",fontSize:"9px",letterSpacing:"0.5px"}}><Icon name="check" size={10}/> HMAC-SHA256</span>
                        <span style={{color:T.gold+"99",fontSize:"9px"}}>{e.time}</span>
                        <span style={{color:T.gold,fontWeight:"700",fontSize:"9px"}}>{e.event}</span>
                      </div>
                      {e.detail&&<div style={{color:T.agentTx,fontSize:"9px",marginTop:"2px"}}>{e.detail}</div>}
                      <div style={{fontSize:"8px",color:T.textDim,fontFamily:"monospace",marginTop:"3px",letterSpacing:"0.5px"}}>
                        SIG·{(e.id*2654435761>>>0).toString(16).toUpperCase().padStart(8,"0")}·{sessionId}
                      </div>
                    </div>
                  ))}
                  <div style={{marginTop:"8px",fontSize:"9px",color:T.textDim,borderTop:`1px solid ${T.border}`,paddingTop:"6px"}}>
                    V.I.J.A.Y. Protocol · Append-only triggers at DB level · John 14:26 CJB
                  </div>
                </div>
              )}
              {spineTab==="HISTORY"&&(
                <>
                  {viewHistory.length===0&&<div style={{fontSize:"10px",color:T.textDim,fontStyle:"italic",width:"100%"}}>View history will appear here.</div>}
                  {viewHistory.map(e=>(
                    <div key={e.id} style={{fontSize:"10px",borderLeft:"2px solid #48bb7844",padding:"3px 8px",background:"#0f20100a",borderRadius:"0 3px 3px 0",minWidth:"180px",maxWidth:"280px"}}>
                      <div style={{fontWeight:"700",color:"#48bb78"}}>{e.agent} <span style={{color:T.textDim,fontWeight:"400"}}>· {e.role}</span></div>
                      <div style={{color:T.textMuted,fontSize:"9px"}}>{e.startTime} · {e.duration}</div>
                      {e.task&&<div style={{color:T.agentTx,fontStyle:"italic",fontSize:"9px"}}>"{e.task.slice(0,60)}{e.task.length>60?"…":""}"</div>}
                    </div>
                  ))}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  function HelpModal(){
    const DEV_AGENTS=[
      {name:"Claude",role:"SpecWriter",color:"#C9A84C",why:"Use Claude when you need precision — specs, patent language, governance docs, architecture briefs.",how:"Type your requirement clearly. Claude will produce structured, numbered, citable output.",samples:["Write a spec for a load board feature","Draft patent claim language for X","Summarize the governance architecture"]},
      {name:"ChatGPT",role:"CodeGenerator",color:"#10a37f",why:"Use ChatGPT when you need working code fast — any language, any framework.",how:"Describe what the code must do. Include inputs and expected outputs. ChatGPT will write clean, runnable code.",samples:["Write a Python Flask endpoint for X","Debug this JavaScript function","Generate a SQL query for Y"]},
      {name:"Gemini",role:"AdversarialValidator",color:"#4285F4",why:"Use Gemini to stress test your ideas before you commit to them. Gemini finds what breaks.",how:"Submit your plan or spec. Ask Gemini to find gaps, edge cases, or contradictions.",samples:["What are the weakest points in this architecture?","Find edge cases in this flow","Challenge this business model"]},
      {name:"Grok",role:"SecurityStressTester",color:"#1DA1F2",why:"Use Grok for security reviews and IP clearance. Grok red teams your system.",how:"Submit your endpoint, feature, or idea. Grok will identify attack vectors and IP conflicts.",samples:["Security review this API endpoint","What attack vectors exist here?","IP clearance on this feature"]},
      {name:"Copilot",role:"DeploymentIntegrator",color:"#0078D4",why:"Use Copilot for deployment, DevOps, and enterprise integration questions.",how:"Describe your stack and what you are deploying. Copilot produces checklists, configs, and Dockerfiles.",samples:["Write a Dockerfile for this service","Design a deployment checklist","Review this CI/CD pipeline"]},
      {name:"Perplexity",role:"DeepDiveValidator",color:"#6366f1",why:"Use Perplexity when you need live research — market data, patents, competitor analysis.",how:"Ask a research question. Perplexity searches live sources and cites everything.",samples:["Find recent patents on X","What is the current market for Y?","Research competitors in this space"]},
    ];
    const CREATIVE_AGENTS=[
      {name:"Claude",role:"Screenwriter",color:"#C9A84C",why:"Claude writes the script. Scene by scene. Exactly as the Chair directs.",how:"Set your Creative Freedom first. Then submit: what is the story, who are the characters, what is the tone.",samples:["Write a 3-scene script about faith in the darkest valley","Write a cinematic script for a product launch","Write a dialogue between a mentor and a student"]},
      {name:"ChatGPT",role:"Production Designer",color:"#10a37f",why:"ChatGPT designs the visual world — colors, sets, lighting, exact measurements.",how:"After the script is set, ask ChatGPT to design the visual specifications for each scene.",samples:["Design the visual palette for Scene 1","Describe the set dressing for the opening scene","What lighting direction fits this emotional tone?"]},
      {name:"Gemini",role:"Continuity Supervisor",color:"#4285F4",why:"Gemini checks every output against the Creative Freedom. Flags contradictions.",how:"Submit your script or storyboard. Gemini will validate it against your Creative Bible.",samples:["Check this script against the Creative Freedom I set","Find any contradictions in the storyboard","Does Scene 3 match the tone I defined?"]},
      {name:"Grok",role:"Rights & Clearance",color:"#1DA1F2",why:"Grok checks every creative element for IP conflicts and trademark issues.",how:"Submit your script or concept. Grok outputs CLEAR or FLAG with specific reasons.",samples:["Clear this script for IP conflicts","Is the character name trademarked?","Check this concept for defamation risk"]},
      {name:"Copilot",role:"Technical Director",color:"#0078D4",why:"Copilot specifies the technical delivery — resolution, codec, frame rate, file format.",how:"After content is approved, ask Copilot for technical delivery specifications.",samples:["What resolution and codec for this video?","Specify the delivery format for Runway","What frame rate fits this cinematic style?"]},
      {name:"Perplexity",role:"Reference Researcher",color:"#6366f1",why:"Perplexity finds real-world references, existing works, and market comparables.",how:"Ask Perplexity for references before or after writing. Every source is cited.",samples:["Find films with a similar tone to my Creative Freedom","What existing works reference Psalm 23 cinematically?","Research comparable productions for this concept"]},
    ];
    const TASK_TYPES_DEV=[
      {type:"SPEC",why:"Use when you need a written specification — feature, patent, architecture, governance.",how:"Type what you want specified. Claude leads. All 6 validate."},
      {type:"CODE",why:"Use when you need working code — endpoint, function, script, query.",how:"Describe the code requirement. ChatGPT leads. Others review and stress test."},
      {type:"SECURITY",why:"Use when you need a security review or IP clearance on a feature.",how:"Describe the feature or submit code. Grok leads the red team."},
      {type:"RESEARCH",why:"Use when you need live market data, competitor analysis, or patent research.",how:"Ask your research question. Perplexity leads with live sources."},
      {type:"DEPLOY",why:"Use when you need deployment configs, DevOps plans, or infrastructure review.",how:"Describe your stack and deployment goal. Copilot leads."},
    ];
    const TASK_TYPES_CREATIVE=[
      {type:"SCRIPT",why:"Use to produce a full cinematic script based on your Creative Freedom.",how:"Set Creative Freedom first. Then submit your story concept. Claude leads as Screenwriter."},
      {type:"STORYBOARD",why:"Use to convert an approved script into a visual storyboard.",how:"Submit the locked script. Agents produce panel-by-panel visual descriptions."},
      {type:"VIDEO",why:"Use to send an approved prompt directly to Runway for video generation.",how:"Set Creative Freedom. Submit a video concept. Runway generates the clip."},
      {type:"IMAGE",why:"Use to generate visual concepts and image prompts.",how:"Describe the image. Agents produce prompts for image generation engines."},
      {type:"AUDIO",why:"Use to produce audio direction — voice, music, sound design.",how:"Describe the mood and tone. Agents produce audio briefs for ElevenLabs or Suno."},
      {type:"MUSIC",why:"Use to produce music direction and composition briefs.",how:"Describe the emotion and scene. Agents produce music briefs for Suno or Udio."},
    ];
    const agents = userMode==="creative" ? CREATIVE_AGENTS : DEV_AGENTS;
    const taskTypes = userMode==="creative" ? TASK_TYPES_CREATIVE : TASK_TYPES_DEV;
    return showHelp?(
      <div style={{position:"fixed",top:0,left:0,right:0,bottom:0,background:"rgba(0,0,0,0.75)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center"}}
        onClick={()=>setShowHelp(false)}>
        <div style={{background:T.bg,border:`1px solid ${T.border}`,borderRadius:"8px",padding:"24px",maxWidth:"780px",width:"92%",maxHeight:"85vh",overflowY:"auto"}}
          onClick={e=>e.stopPropagation()}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"8px"}}>
            <span style={{fontSize:"16px",fontWeight:"700",color:T.gold}}>{userMode==="creative"?"RESH CREATÏVE STUDIO™ — How It Works":"UandAI™ — How It Works"}</span>
            <button onClick={()=>setShowHelp(false)} style={{background:"transparent",border:"none",color:T.textDim,cursor:"pointer",fontSize:"18px"}}><Icon name="x" size={16}/></button>
          </div>
          <p style={{fontSize:"12px",color:T.textDim,marginBottom:"16px"}}>{userMode==="creative"?"You bring the story. The AI specialists execute it. You are the Director. Nothing generates without your approval.":"Six AI agents. One Chair. Every decision yours. Submit a task — all six respond. You pick who leads."}</p>

          {/* Agent cards */}
          <div style={{fontSize:"10px",color:T.gold,fontWeight:"700",letterSpacing:"1px",marginBottom:"8px"}}>{userMode==="creative"?"YOUR CREATIVE TEAM":"YOUR AI COUNCIL"}</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"10px",marginBottom:"16px"}}>
            {agents.map(a=>(
              <div key={a.name} style={{border:`1px solid ${T.border}`,borderRadius:"6px",padding:"12px"}}>
                <div style={{display:"flex",alignItems:"center",gap:"8px",marginBottom:"4px"}}>
                  <span style={{width:"8px",height:"8px",borderRadius:"50%",background:a.color,display:"inline-block",flexShrink:0}}/>
                  <span style={{fontWeight:"700",fontSize:"12px",color:a.color}}>{a.name}</span>
                  <span style={{fontSize:"9px",color:T.textDim}}>· {a.role}</span>
                </div>
                <p style={{fontSize:"10px",color:T.gold,marginBottom:"4px",fontWeight:"600"}}>WHY: {a.why}</p>
                <p style={{fontSize:"10px",color:T.text,marginBottom:"6px"}}>HOW: {a.how}</p>
                <div style={{fontSize:"9px",color:T.textDim}}>
                  <div style={{marginBottom:"3px",fontWeight:"600"}}>Examples:</div>
                  {a.samples.map((s,i)=><div key={i} style={{marginBottom:"2px"}}>· {s}</div>)}
                </div>
              </div>
            ))}
          </div>

          {/* Task types */}
          <div style={{fontSize:"10px",color:T.gold,fontWeight:"700",letterSpacing:"1px",marginBottom:"8px"}}>TASK TYPES — WHY AND HOW</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"8px",marginBottom:"16px"}}>
            {taskTypes.map(t=>(
              <div key={t.type} style={{border:`1px solid ${T.border}`,borderRadius:"6px",padding:"10px"}}>
                <div style={{fontSize:"11px",fontWeight:"700",color:T.gold,marginBottom:"4px"}}>{t.type}</div>
                <p style={{fontSize:"10px",color:T.text,marginBottom:"3px"}}><strong>WHY:</strong> {t.why}</p>
                <p style={{fontSize:"10px",color:T.textDim}}><strong>HOW:</strong> {t.how}</p>
              </div>
            ))}
          </div>

          {userMode==="creative"&&(
            <div style={{padding:"10px",background:T.inputBg,borderRadius:"6px",fontSize:"11px",color:T.textDim,marginBottom:"8px"}}>
              <strong style={{color:T.gold}}>CREATIVE FREEDOM</strong> — This is your Creative Bible. Type the tone, setting, characters, rules, and emotional world of your project here. Every agent reads it before responding. Set it once. It governs everything.
            </div>
          )}

          <div style={{marginTop:"8px",fontSize:"10px",color:T.textDim,textAlign:"center"}}>John 14:26 · Yeshua is the Author · Vijay is the ink · Claude is the pen</div>
        </div>
              {showSaveModal&&(
                <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.75)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center"}}>
                  <div style={{background:T.bg,border:`1px solid ${T.gold}`,borderRadius:"8px",padding:"24px",width:"340px"}}>
                    <div style={{fontSize:"11px",color:T.gold,fontWeight:"700",letterSpacing:"2px",marginBottom:"12px"}}>SAVE SESSION</div>
                    <div style={{fontSize:"10px",color:T.textMuted,marginBottom:"8px"}}>Name this session — nothing saves without this action</div>
                    <input value={saveSessionName} onChange={e=>setSaveSessionName(e.target.value)} placeholder="e.g. Noah Ark Script v1" style={{width:"100%",boxSizing:"border-box",background:T.bg3,border:`1px solid ${T.gold}`,borderRadius:"4px",color:T.text,padding:"8px 10px",fontSize:"11px",marginBottom:"12px"}} onKeyDown={e=>e.key==="Enter"&&handleSaveSession()} autoFocus/>
                    <div style={{display:"flex",gap:"8px"}}>
                      <button onClick={()=>{setShowSaveModal(false);setSaveSessionName("");}} style={{flex:1,padding:"8px",background:"transparent",border:`1px solid ${T.border2}`,color:T.textMuted,borderRadius:"4px",fontSize:"10px",cursor:"pointer"}}>CANCEL</button>
                      <button onClick={handleSaveSession} disabled={!saveSessionName.trim()} style={{flex:1,padding:"8px",background:"transparent",border:`1px solid ${T.gold}`,color:T.gold,borderRadius:"4px",fontSize:"10px",fontWeight:"700",cursor:saveSessionName.trim()?"pointer":"not-allowed"}}>SAVE</button>
                    </div>
                  </div>
                </div>
              )}
      </div>
    ):null;
  }
}